/** MASA DE LUCRU A EDITORULUI DE PLAN — una singură, pentru două pagini.
 *
 *  Paleta la stânga, pânza (sau macheta) la mijloc, proprietățile la dreapta,
 *  bara selecției peste obiectul ales, tastele, istoricul, gesturile. Au stat
 *  în pagina șablonului de tronson (`pages/Editor`) până când același editor a
 *  trebuit să deschidă și un NIVEL AL SOLUȚIEI ARHITECTURALE (Stefan,
 *  23.09.2026: «să funcționeze în același mod, inclusiv să te lase să tragi de
 *  obiecte și să salveze în soluție»).
 *
 *  DE CE E SCOASĂ ÎNTR-UN LOC ȘI NU COPIATĂ. Cerința a doua a fost «când
 *  modificăm codul pentru tronsoane să se modifice și în arhitectură, și
 *  invers». Două copii ale acestui fișier s-ar fi despărțit la prima unealtă
 *  nouă. Așa, o unealtă, o tastă sau un rând de panou pus aici apare în
 *  amândouă paginile, iar ce le deosebește — de unde vine documentul și unde
 *  se scrie — rămâne în pagini:
 *
 *    * `pages/Editor` — șablonul din catalog: ciornă, publicare, etichete,
 *      asistentul;
 *    * `plan/EditorNivel` — nivelul unei soluții, împachetat ca document de
 *      tronson (`plan/nivelTronson.ts`) și rescris pe loc în soluție.
 *
 *  `useMasa` ține starea; `MasaDeLucru` o desenează. Separate, fiindcă pagina
 *  are nevoie de stare (documentul de trimis, `aplica` pentru asistent) și în
 *  afara corpului pe care îl desenează masa.
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState,
         type ReactNode } from "react";
import { deriva, H_NIVEL, type TronsonDoc } from "../doc";
import { Icoana } from "../icoane";
import Panza, { type Unealta, type Vecin } from "./Panza";
import { COMENZI_REVIT, INCEPUTURI, RASTIMP_COMANDA_MS } from "./comenzi";
import Paleta from "./Paleta";
import PaletaDesign from "../design/PaletaDesign";
import Proprietati from "./Proprietati";
import { BaraCalcului } from "../Calcul";
import { calcNou, imagineDinFisier, type Calc } from "../calc";
import { iaCalcul, puneCalcul } from "../calcStocare";
import {
  curbeazaPeretele, dubleaza,
  intoarcerile, oglindesteObiectul, rotesteObiectul, Istoric,
  type Intoarcere,
  roteastePeretele, unghiulPeretelui,
  mutaAparat, mutaCapatulSingur, mutaNucleul,
  mutaParteaNucleului, mutaPereteleSingur, schimbaBanda, schimbaBratul,
  faNucleu, parteDinPiesa, piesaDinParte, schimbaCategoria, schimbaFrontul, schimbaNucleul,
  schimbaParteaNucleului,
  schimbaScara, schimbaLungimeaTotala, schimbaLatimeaTotala, schimbaLatimeaRampei,
  adaugaPodest,
  schimbaScaraParte,
  mutaGolulLiber, osiaPeretelui, pictaFinisaj, schimbaAparat, schimbaCamera,
  schimbaExteriorul, adaugaInDoza, scoateDinDoza, schimbaIndiceInDoza,
  axaPeretelui, capetePrinse, schimbaGolul, schimbaMobila, schimbaPeretele,
  sterge,
  uneste, uneMobila,
  separaMobila, rangMobilaMulte, rangPeretiMulti, stergeAlesele,
  capeteLibere, lasaCapeteleLibere,
  mutaMobilaMulte, grupulLui,
  cateUnaPeGrup,
  type Ales,
  adaugaAparatInCamera, dezleagaAparatul, puneInstalatiaCamerei,
  adaugaStrat, adaugaAxul, areAxa, golRatacit, lipesteGolulDeZid,
  intoarcePeretele, mutaStratul,
  schimbaStratul, stergeStratul, axele, lungimeaAxei, intindereaAxei,
  cutiaDesenului,
  cuCoteleStinseMutate,
  lungimeaAxeiLibera, blocheazaAxul, axaBlocata } from "./model";
import { mobileazaAutomat } from "../design/auto";
import "../design/design.css";
import { cuCamereDerivate, reperulCamerelor, type ReperCamere } from "./camere";
import { conturulDupaDesen, mobileazaCamera } from "./model";
import { indreaptaPeretele45, squareWalls, type RepairReport } from "./repair45";
import { lungimeaInelului, unghiulDinCitit } from "./wallAngles";
import { diferenta, programeaza, type Aratare } from "./aratare";
import LayersPanel from "./LayersPanel";
import {
  clampLayer, editable, LAYER_IDS, layerOfPiece, layerOfTool, layersOfItem, loadLayers, saveLayers,
  type Layer, type LayerId, type Layere,
} from "./layers";
import type { Eticheta } from "../../../api";
import "./editor.css";
import { t as tr, tn as trn, useT } from "../../../i18n";
import type { Cheie } from "../../../i18n";

// `three` intră în bundle doar când cineva chiar deschide macheta.
const Randare3D = lazy(() => import("../Randare3D"));




/** Numele fiecărei unelte, pe cheia ei. Tabelul stă lângă tipul `Unealta`: o
 *  unealtă nouă se adaugă aici și nu poate rămâne fără nume. */
const NUME_UNEALTA: Record<Exclude<Unealta, "sageata" | "pensula">, Cheie> = {
  perete: "editor.unealta.perete",
  demoleaza: "editor.unealta.demoleaza",
  arc: "editor.unealta.arc",
  parapet: "editor.unealta.parapet",
  balcon: "editor.unealta.balcon",
  usa: "editor.unealta.usa",
  usaDubla: "editor.unealta.usaDubla",
  usaGlisanta: "editor.unealta.usaGlisanta",
  fereastra: "editor.unealta.fereastra",
  camera: "editor.unealta.camera",
  cameraL: "editor.unealta.cameraL",
  cameraT: "editor.unealta.cameraT",
  cameraLibera: "editor.unealta.cameraLibera",
  cota: "editor.unealta.cota",
  intinde: "editor.unealta.intinde",
  zonaDesfumare: "editor.unealta.zonaDesfumare",
  cameraFaraPereti: "editor.unealta.cameraFaraPereti",
  scara: "paleta.scara_custom",
  scaraGata_dreapta: "paleta.scara_gata_dreapta",
  scaraGata_dreapta_podest: "paleta.scara_gata_dreapta_podest",
  scaraGata_u: "paleta.scara_gata_u",
  scaraGata_u_doua: "paleta.scara_gata_u_doua",
  lift: "editor.unealta.lift",
  ghena: "editor.unealta.ghena",
  mobila: "editor.unealta.mobila",
  aparat: "editor.unealta.aparat",
};

/** Ce faci cu unealta aleasă, într-o propoziție. */
const SFAT_UNEALTA: Record<Exclude<Unealta, "sageata">, Cheie> = {
  perete: "editor.sfat.perete",
  demoleaza: "editor.sfat.demoleaza",
  arc: "editor.sfat.perete",
  parapet: "editor.sfat.parapet",
  balcon: "editor.sfat.balcon",
  cameraLibera: "editor.sfat.camera_libera",
  zonaDesfumare: "editor.sfat.zona_desfumare",
  cameraFaraPereti: "editor.sfat.camera_fara_pereti",
  cota: "editor.sfat.cota",
  intinde: "editor.sfat.intinde",
  camera: "editor.sfat.camera",
  cameraL: "editor.sfat.camera",
  cameraT: "editor.sfat.camera",
  scara: "editor.sfat.scara",
  scaraGata_dreapta: "editor.sfat.scara_gata",
  scaraGata_dreapta_podest: "editor.sfat.scara_gata",
  scaraGata_u: "editor.sfat.scara_gata",
  scaraGata_u_doua: "editor.sfat.scara_gata",
  lift: "editor.sfat.piesa",
  ghena: "editor.sfat.piesa",
  mobila: "editor.sfat.mobila",
  aparat: "editor.sfat.aparat",
  pensula: "editor.sfat.pensula",
  usa: "editor.sfat.gol",
  usaDubla: "editor.sfat.gol",
  usaGlisanta: "editor.sfat.gol",
  fereastra: "editor.sfat.gol",
};


/** CE DOCUMENT E PE MASĂ. Aproape tot ce face masa e același pe amândouă, iar
 *  puținul care diferă se citește de aici, nu dintr-un steag împrăștiat:
 *
 *    * `tronson` — șablonul: panoul vorbește despre modul (front, bandă,
 *      etichete), conturul are front și bandă;
 *    * `nivel` — un nivel de soluție: n-are front, bandă sau etichete de
 *      catalog, iar panoul fără nimic ales vorbește despre nivel. */
export type FelMasa = "tronson" | "nivel";



export type OptiuniMasa = {
  /** Cine are voie să scrie — tastele care schimbă ceva se sting fără el. */
  potScrie: boolean;
  /** Ce fel de masă e — v. `FelMasa`. Numai pe TRONSON merg comenzile din două
   *  litere ale Revit-ului; pe nivel tastele rămân cum erau (hotărât
   *  04.10.2026: «the keys only change for tronsoane, for the rest of the tabs
   *  they stay the same»). */
  fel?: FelMasa;
  /** Unde se ține hârtia de calc pe aparat: `[proiect, cheie]` — codul
   *  modulului pe tronson, nivelul soluției pe arhitectură. */
  cheieCalc: [string, string];
};

/** MAI EXISTĂ OBIECTUL ALES în documentul `d`?
 *
 *  Un pas de istoric poate scoate chiar obiectul aprins — un ⌘Z peste o creare
 *  îl desface la loc —, iar indicele lui ar rămâne atunci în gol: panoul ar
 *  arăta măsurile ALTUIA, pe poziția eliberată, sau ale nimănui. Deci selecția
 *  se păstrează numai dacă are ce ține.
 *
 *  SE ÎNTREABĂ DESPRE FIECARE FEL PE NUME, și felurile pe care nu le cunoaște
 *  pică pe `false`: selecția ștearsă e purtarea de dinainte, adică cel mai rău
 *  lucru care se poate întâmpla e că rămâne cum era. */
function maiExista(d: TronsonDoc, a: Ales): boolean {
  if (!a) return false;
  const p = d.plan;
  if (!p) return false;
  switch (a.fel) {
    case "perete": return !!p.walls?.[a.i];
    case "camera": return !!p.rooms?.[a.i];
    case "gol": return !!p[a.fel2]?.[a.i];
    case "mobila": return !!p.furniture?.[a.i];
    case "aparat": return !!p.electrical?.[a.i];
    case "nucleuParte": return !!p.core_parts?.[a.i];
    case "ax": return a.i < axele(d).length;
    // Anvelopa și nucleul nu se șterg dintr-un pas de istoric obișnuit: ele
    // sunt ale modulului, nu obiecte puse în el.
    case "anvelopa": return true;
    case "nucleu": return !!p.core_parts?.length || !!p.core_rect;
    case "scaraParte": {
      const sc = p.furniture?.[a.i]?.scara;
      if (!sc?.rampe?.length) return false;
      // un podest stă ÎNTRE două rampe, deci are nevoie de rampa de după el
      return a.parte === "rampa" ? a.k < sc.rampe.length
                                 : a.k + 1 < sc.rampe.length;
    }
    default: return false;
  }
}

export function useMasa({ potScrie, cheieCalc, fel = "tronson" }: OptiuniMasa) {
  const [projectId, code] = cheieCalc;
  const [doc, setDoc] = useState<TronsonDoc | null>(null);
  const [ales, setAles] = useState<Ales>(null);
  /** CAMERELE ALESE, toate. `ales` rămâne una singură — cea al cărei panou se
   *  citește la dreapta și pe care stau apucătoarele —, iar lista asta e ce
   *  s-a adunat cu ⌘: din ea lucrează gesturile care cer mai multe camere
   *  deodată, unirea în primul rând. Goală pentru orice altceva decât o
   *  cameră. */
  const [alese, setAlese] = useState<number[]>([]);
  const [unealta, setUnealta] = useState<Unealta>("sageata");
  /** Ce piesă pune unealta «Mobilier». Ține minte alegerea: cine mobilează
   *  pune de obicei mai multe scaune la rând, nu unul din fiecare fel. */
  const [piesa, setPiesa] = useState("canapea");
  /** Paleta strânsă la glife. Pe un laptop, cei 186 px ai ei sunt cinci metri
   *  de clădire în plus pe ecran. */
  const [stransa, setStransa] = useState(false);
  /** …și panoul din dreapta, strâns la fel. Cerut de arhitect (26.10.2026):
   *  «să poți să închizi temporar meniul din dreapta la editare». Stă aici,
   *  lângă starea paletei, fiindcă grila editorului le citește pe amândouă. */
  const [panouStrans, setPanouStrans] = useState(false);
  /** De câte ori s-a cerut încadrarea din taste (`ZA`). Pânza reîncadrează când
   *  i se schimbă cheia, iar cheia o dă pagina — deci numărul ăsta se lipește
   *  de ea (v. `MasaDeLucru`), și o apăsare pe ZA e o cheie nouă. */
  const [incadrari, setIncadrari] = useState(0);
  // MACHETA E ÎN EDITOR, nu doar în vizualizator: înălțimea unui perete și un
  // parapet care se oprește sub cot nu se pot judeca pe un plan — planul e o
  // secțiune, iar tot ce e sub ea arată la fel. Se editează în 2D și se
  // verifică în 3D, fără să pleci din pagină.
  // TREI VEDERI, NU DOUĂ. «Plan» e planșa tehnică — pereți, goluri, cote —, pe
  // care se compartimentează. «Design» e aceeași geometrie văzută ca amenajare:
  // pardoseala fiecărei camere cu finisajul ei, mobilierul, instalația și
  // traseele firelor. «Machetă» le arată pe amândouă în volum. Nu sunt trei
  // documente: e același document, cu alte straturi aprinse — un perete mutat
  // în design se mută și pe planșă, fiindcă e același perete.
  const [vedere, setVedere] = useState<"2d" | "3d">("2d");
  /** MODUL E SEPARAT DE VEDERE, și separarea e ce face macheta folositoare.
   *
   *  Cine tocmai a pus prizele apasă «Machetă» ca să vadă pe unde urcă firele —
   *  nu ca să se întoarcă la pereți goi. Deci macheta păstrează modul din care
   *  s-a intrat în ea, iar butoanele «Plan» și «Design» sunt cele care îl
   *  schimbă. Trei butoane pe ecran, două stări dedesubt: e aceeași mișcare
   *  pentru om și o stare mai puțin de ținut minte. */
  const [mod, setMod] = useState<"plan" | "design">("plan");
  /** Ce aparat pune unealta «Electrice». */
  const [aparat, setAparat] = useState("priza");
  /** Cu ce vopsește pensula. */
  const [finisaj, setFinisaj] = useState<{ unde: "floor" | "wall"; cheie: string }>(
    { unde: "floor", cheie: "parchet_stejar" });
  /** Ce s-a pus ultima dată cu bagheta — se arată sub bara de sus, o dată. */
  const [raportBagheta, setRaportBagheta] = useState<string | null>(null);
  /** CE TOCMAI A SCHIMBAT ASISTENTUL, cât se vede — v. `editor/aratare.ts`.
   *  Gesturile mâinii n-o pun niciodată: mâna știe pe ce a apăsat. */
  const [aratare, setAratare] = useState<Aratare | null>(null);

  /** HÂRTIA DE CALC a modulului — imaginea peste care se trasează.
   *
   *  NU E A DOCUMENTULUI, deci nu trece prin istoric, nu marchează nesalvat și
   *  nu se salvează pe server: e o foaie a mesei de lucru, ținută pe aparat
   *  (`viewer/tronson/calc.ts`). Se schimbă cu `schimbaCalcul`, care o scrie și
   *  în `localStorage` — o panoramare de-o oră peste un plan scanat n-are de ce
   *  să se piardă la o reîncărcare a paginii. */
  const [calc, setCalc] = useState<Calc | null>(null);

  /** Axele de trasare se arată doar la cerere — v. butonul din bara de sus. */
  const [araAxe, setAraAxe] = useState(false);
  const [araEtichete, setAraEtichete] = useState(true);
  /** THE VIEW LAYERS (`editor/layers`): walls, furniture, sanitary, sockets,
   *  lights and switches, floors — each on or off, at an opacity. View state:
   *  never in the document. */
  const [layere, setLayere] = useState<Layere>(loadLayers);
  /** The layers panel, open or closed; closed at every start. */
  const [layereDeschise, setLayereDeschise] = useState(false);
  const layereRef = useRef(layere);
  layereRef.current = layere;
  /** COTELE ȘI APARTAMENTELE, comutatoarele NIVELULUI de soluție (v.
   *  `ComutatoareMasa`). Cotele pornesc aprinse, ca pe tronson; spălătura
   *  apartamentelor stinsă, ca pe desenul nivelului: e o verificare pe care
   *  o ceri, nu planșa. */
  const [araCote, setAraCote] = useState(true);
  const [araApartamente, setAraApartamente] = useState(false);
  const istoric = useRef(new Istoric());
  const [, redeseneaza] = useState(0);

  const plan = useMemo(() => (doc ? deriva(doc) : null), [doc]);

  /** Documentul curent, citibil din afara unui `setDoc`.
   *
   *  ISTORICUL NU ARE VOIE SĂ SE SCHIMBE ÎNTR-UN UPDATER de stare. React 18 în
   *  `StrictMode` cheamă updaterul de DOUĂ ori, ca să scoată la iveală exact
   *  efectele laterale ascunse în el — iar un teanc pe care apeși de două ori
   *  face altceva decât ai cerut. Măsurat aici: ⌘Z mergea, dar ⇧⌘Z nu, fiindcă
   *  a doua chemare a lui `refa` găsea teancul deja golit de prima și întorcea
   *  `null`, adică documentul de dinainte. Deci teancul se atinge în afara
   *  updaterului, pe valoarea din `ref`. */
  const docRef = useRef<TronsonDoc | null>(null);
  /** Reperul camerelor, luat la deschidere. Vezi `reperulCamerelor`. */
  const bazaRef = useRef<ReperCamere | null>(null);
  docRef.current = doc;
  /** Ce e ales, citibil din ascultătorul de taste fără să-l relege la
   *  fiecare selecție. */
  const alesRef = useRef<Ales>(null);
  alesRef.current = ales;
  const aleseRef = useRef<number[]>([]);
  aleseRef.current = alese;
  /** PEREȚII ALEȘI, pentru trasul mai multora deodată. Listă proprie, nu
   *  `alese`: aia e a camerelor și o citește «Unește», care n-are ce face cu
   *  ziduri. Două liste scurte, fiecare cu rostul ei, în loc de una cu fel. */
  const [pereteleAlese, setPereteleAlese] = useState<number[]>([]);
  /** ZIDURILE TOCMAI DUBLATE, cât timp copia e încă în mână.
   *
   *  O copie se naște LIPITĂ de rețea: capetele ei cad pe fețele altor ziduri,
   *  iar tragerea lungește vecinii ca la orice perete așezat (`cuVecinii` ->
   *  `lungesteVecinii`) — adică plasa se deformează în jurul unei piese pe care
   *  omul abia o așază. Cerut de arhitect (26.10.2026): «când dublez un perete
   *  nu vreau să se agațe din start de cei de lângă el, pentru că vreau să-l
   *  mut». Nicio decalare nu rezolvă asta: măsurat pe M2, copia cade lângă ceva
   *  oriunde ai pune-o (43 de cazuri din 34 de ziduri, pe amândouă axele).
   *
   *  Starea stă ÎN EDITOR, nu în document: e o însușire a gestului, nu a
   *  clădirii, și n-are ce căuta într-o salvare. Se stinge la prima alegere a
   *  altceva — adică exact cât ține «din start». */
  /** CU FELUL LOR, nu doar indicii: o copie de zid și una de mobilier pot avea
   *  același număr, iar o listă fără fel ar fi stins lipirea zidului 7 fiindcă
   *  piesa 7 tocmai fusese dublată. */
  const [dublateAcum, setDublateAcum] =
    useState<{ fel: "perete" | "mobila"; indici: number[] } | null>(null);
  const pereteleAleseRef = useRef<number[]>([]);
  pereteleAleseRef.current = pereteleAlese;
  /** PIESELE ALESE, pentru «Unește elemente». A treia listă scurtă, din
   *  același motiv ca a doua: fiecare fel de selecție multiplă are gestul ei,
   *  iar o listă comună cu un câmp «fel» ar fi cerut o verificare la fiecare
   *  citire. Cerut pe 19.10.2026: «sa ai si opțiunea de mulți select mobila si
   *  sa apeși unește elemente». */
  const [mobilaAlese, setMobilaAlese] = useState<number[]>([]);
  const mobilaAleseRef = useRef<number[]>([]);
  mobilaAleseRef.current = mobilaAlese;

  /** Un gol din selecția multiplă: lista lui și numărul lui ÎN ea. */
  type GolAles = { fel2: "doors" | "windows"; i: number };
  /** GOLURILE ALESE — uși și ferestre adunate cu ⇧. A patra listă scurtă, din
   *  același motiv ca celelalte. Cerut de arhitect (22.10.2026): «vreau să pot
   *  selecta mai multe uși deodată».
   *
   *  POARTĂ ȘI LISTA, nu doar indicele: un gol se numește `(fel2, i)` — `i` e
   *  numărul lui ÎN LISTA LUI —, deci ușa 3 și fereastra 3 sunt lucruri
   *  deosebite care ar fi ajuns amândouă «3». */
  const [golurileAlese, setGolurileAlese] = useState<GolAles[]>([]);
  const golurileAleseRef = useRef<GolAles[]>([]);
  golurileAleseRef.current = golurileAlese;

  /** ALEGE — cu Shift pe camere și ziduri, cu ⌘ pe mobilă, adună.
   *
   *  O singură cale pentru amândouă felurile de selecție: fără ea, cele două
   *  stări (camera curentă și lista) s-ar fi putut contrazice — o listă rămasă
   *  din gestul de dinainte, cu panoul pe altă cameră, iar «Unește» ar fi
   *  lucrat pe ce nu se mai vede aprins.
   *
   *  TASTA DIFERĂ PE FELURI, și n-am avut încotro: pe o piesă de mobilier
   *  Shift ținut o ROTEȘTE de mult (v. `Panza::laObiect`), deci acolo adună
   *  ⌘. Pe camere și ziduri rămâne Shift, cum s-a cerut.
   *
   *  A doua apăsare pe un obiect deja ales îl SCOATE din selecție, ca peste
   *  tot: altfel greșeala de a apăsa unul în plus n-ar avea întoarcere decât
   *  reluând tot. */
  const alege = useCallback((a: Ales, adauga = false) => {
    // UN GOL RĂTĂCIT SE LIPEȘTE DE ZID CÂND E ALES.
    //
    // O ușă nu se poate PUNE în mijlocul camerei (`adaugaGol` refuză fără
    // gazdă), dar poate AJUNGE acolo: citită așa la import, sau lăsată în urmă
    // de zidul mutat de sub ea. Atunci plutește, iar arcul ei nu taie nimic.
    //
    // LA ALEGERE, nu la desenare: o reparație făcută tăcut la fiecare randare
    // ar muta ușa fără ca nimeni s-o fi cerut. Aleasă, omul vede saltul și
    // poate să-l anuleze — reașezarea intră în istoric ca orice altă schimbare.
    // După ce s-a lipit, se mută pe lungul zidului ca orice gol.
    if (a?.fel === "gol" && potScrie) {
      const d = docRef.current;
      if (d && golRatacit(d, a.fel2, a.i)) {
        const reasezat = lipesteGolulDeZid(d, a.fel2, a.i);
        if (reasezat !== d) {
          istoric.current.pune(d);
          setDoc(reasezat);
          redeseneaza((n) => n + 1);
        }
      }
    }
    if (adauga && a?.fel === "camera") {
      const acum = aleseRef.current;
      const fara = acum.filter((x) => x !== a.i);
      const nou = fara.length === acum.length ? [...acum, a.i] : fara;
      setAlese(nou);
      setPereteleAlese([]);
      setAles(nou.length ? { fel: "camera", i: nou[nou.length - 1] } : null);
      return;
    }
    // ACELAȘI GEST, PE ZIDURI: ⌘ adună, ⌘ pe unul deja ales îl scoate. Cerut
    // ca «group select walls to pull them at the same time» — un rând de
    // compartimentări care trebuie mutate cu aceeași cotă se alege o dată și
    // se trage o dată, în loc de patru trageri nimerite la fel.
    if (adauga && a?.fel === "perete") {
      const acum = pereteleAleseRef.current;
      const fara = acum.filter((x) => x !== a.i);
      const nou = fara.length === acum.length ? [...acum, a.i] : fara;
      setPereteleAlese(nou);
      setAlese([]);
      setAles(nou.length ? { fel: "perete", i: nou[nou.length - 1] }
        : mobilaAleseRef.current.length
          ? { fel: "mobila", i: mobilaAleseRef.current[0] } : null);
      return;
    }
    // ACELAȘI GEST, PE GOLURI: ⇧ adună, ⇧ pe unul deja ales îl scoate. Cerut
    // de arhitect (22.10.2026): «vreau să pot selecta mai multe uși deodată» —
    // un șir de uși de aceeași mărime se alege o dată și se șterge o dată, în
    // loc de patru clicuri și patru ștergeri.
    //
    // CU TOT CU LISTA: `i` e numărul golului în lista LUI, deci ușa 3 și
    // fereastra 3 sunt lucruri deosebite.
    if (adauga && a?.fel === "gol") {
      const acum = golurileAleseRef.current;
      const fara = acum.filter((x) => !(x.fel2 === a.fel2 && x.i === a.i));
      const nou = fara.length === acum.length
        ? [...acum, { fel2: a.fel2, i: a.i }] : fara;
      setGolurileAlese(nou);
      setAlese([]);
      const ultim = nou[nou.length - 1];
      setAles(ultim ? { fel: "gol", fel2: ultim.fel2, i: ultim.i } : null);
      return;
    }
    // ACELAȘI GEST, PE MOBILĂ. ⌘ adună, ⌘ pe una deja aleasă o scoate — e
    // selecția din care se cere «Unește elemente».
    // ZIDURI ȘI PIESE ÎN ACEEAȘI SELECȚIE. Cerut de arhitect (22.10.2026):
    // «vreau să pot să selectez cu shift și piese și pereți în același timp, să
    // mut mobilier cu tot cu pereți în anumite cazuri».
    //
    // Deci ⇧ pe o piesă NU mai stinge zidurile adunate (și n-a stins niciodată
    // piesele când ⇧ cădea pe un zid — asimetria era chiar defectul). Camerele
    // rămân deoparte: ele nu se mută deloc, nici singure, nici cu altceva
    // (v. antetul lui `editor/Panza`), deci o cameră în aceeași listă n-ar
    // însemna nimic.
    //
    // UN CLIC FĂRĂ ⇧ GOLEȘTE TOT, ca până acum: selecția mixtă se face numai
    // adunând dinadins.
    if (adauga && a?.fel === "mobila") {
      const acum = mobilaAleseRef.current;
      const fara = acum.filter((x) => x !== a.i);
      const nou = fara.length === acum.length ? [...acum, a.i] : fara;
      setMobilaAlese(nou);
      setAlese([]);
      setAles(nou.length ? { fel: "mobila", i: nou[nou.length - 1] }
        : pereteleAleseRef.current.length
          ? { fel: "perete", i: pereteleAleseRef.current[0] } : null);
      return;
    }
    setAles(a);
    // UN CLIC PE CEVA DEJA ALES NU STRICĂ SELECȚIA — cerut de arhitect
    // (21.10.2026): «if I click on a selected item it should let me move them
    // together (...) I should click away on another item without shift to
    // deselect».
    //
    // Fără asta, selecția multiplă nu se putea FOLOSI: adunai cinci piese cu
    // Shift, puneai degetul pe una ca s-o tragi — iar apăsarea, fiind fără
    // Shift, cădea aici și golea lista, lăsând piesa aia singură. Gestul de
    // mutare apuca apoi exact o piesă, deci grupul se destrăma în clipa în care
    // voiai să-l miști. Modelul era bun, drumul mâinii nu.
    //
    // DESFACEREA RĂMÂNE UNDE SE AȘTEAPTĂ: un clic pe ALTCEVA — altă piesă, alt
    // zid, pânza goală — trece pe lângă poarta asta și golește lista, fiindcă
    // ce s-a apăsat nu e în ea.
    const inLista = a?.fel === "camera" ? aleseRef.current.includes(a.i)
      : a?.fel === "perete" ? pereteleAleseRef.current.includes(a.i)
      : a?.fel === "mobila" ? mobilaAleseRef.current.includes(a.i)
      : a?.fel === "gol" ? golurileAleseRef.current.some(
        (x) => x.fel2 === a.fel2 && x.i === a.i)
      : false;
    if (inLista) return;
    setAlese(a?.fel === "camera" ? [a.i] : []);
    // Un zid ales simplu rămâne singur în listă, ca trasul lui să treacă pe
    // aceeași cale ca al mai multora.
    setPereteleAlese(a?.fel === "perete" ? [a.i] : []);
    // Alegerea altui lucru încheie «din start»-ul copiei.
    setDublateAcum((v) => (v ? null : v));
    // Un gol ales simplu rămâne singur în listă, ca ștergerea lui să treacă pe
    // aceeași cale ca a mai multora.
    setGolurileAlese(a?.fel === "gol" ? [{ fel2: a.fel2, i: a.i }] : []);
    // UN ANSAMBLU SE APRINDE ÎNTREG, nu doar piesa apăsată. Cerut de arhitect
    // (21.10.2026): «dacă mai multe obiecte sunt grupate/unite aș vrea să se
    // vadă asta și în selecție; poate ar trebui să arate ca atunci când sunt
    // selectate mai multe obiecte».
    //
    // ERA DEJA ADEVĂRAT LA MUTARE, doar nevăzut: trasă de o piesă, un ansamblu
    // pleacă tot (`cePleacaCu`), deci aprinderea unei singure piese MINȚEA —
    // omul apuca una și se mișcau cinci. Acum lista spune ce se va întâmpla.
    //
    // ȘI FAPTELE URMEAZĂ APRINDEREA, fiindcă `aleseleToate` citește tocmai
    // lista asta: ștergerea, dublarea și rangul prind de-acum tot ansamblul. E
    // chiar ce înseamnă să unești niște piese — cine a legat o masă cu patru
    // scaune și apasă Șterge nu vrea să rămână cu scaunele.
    setMobilaAlese(a?.fel === "mobila"
      ? (docRef.current ? grupulLui(docRef.current, a.i) : [a.i]) : []);
  }, []);

  /** The layers that must be at 100% to edit what `a` names, in `p`. */
  const layereleAlegerii = (a: Ales, p: typeof plan): LayerId[] => {
    if (!a || !p) return [];
    if (a.fel === "perete" || a.fel === "gol") return ["walls"];
    if (a.fel === "mobila") {
      const l = p.piese[a.i] ? layerOfPiece(p.piese[a.i].kind) : null;
      return l ? [l] : [];
    }
    if (a.fel === "aparat") {
      const e = p.electrice.find((x) => x.idx === a.i);
      return e ? layersOfItem(e) : [];
    }
    return [];
  };

  /** CHANGES ONE VIEW LAYER. A layer that stops being editable first drops
   *  the selection it holds, and the tool in hand that puts objects on it
   *  goes back to the arrow: arrows, Delete or a click would otherwise act on
   *  what nobody can see or grab. Moving the slider of an off layer turns it
   *  on. */
  const schimbaLayerul = useCallback((id: LayerId, patch: Partial<Layer>) => {
    const vechi = layereRef.current[id];
    const nou = clampLayer(id, {
      ...vechi, ...patch, ...("opacity" in patch && !("on" in patch) ? { on: true } : {}),
    });
    if (editable(vechi) && !editable(nou)) {
      const tine = layereleAlegerii(ales, plan).includes(id)
        || (id === "walls" && (pereteleAlese.length > 0 || golurileAlese.length > 0))
        || mobilaAlese.some((i) => plan?.piese[i] && layerOfPiece(plan.piese[i].kind) === id);
      if (tine) alege(null);
      if (layerOfTool(unealta, piesa, aparat) === id) setUnealta("sageata");
    }
    layereRef.current = { ...layereRef.current, [id]: nou };
    setLayere(layereRef.current);
    saveLayers(layereRef.current);
  }, [ales, plan, pereteleAlese, golurileAlese, mobilaAlese, alege, unealta, piesa, aparat]);

  /** Brings the given layers back to on at 100%, those that are not. */
  const aprindeLayerele = useCallback((ids: readonly LayerId[]) => {
    for (const id of ids) {
      if (!editable(layereRef.current[id])) schimbaLayerul(id, { on: true, opacity: 1 });
    }
  }, [schimbaLayerul]);

  // A selection of what cannot be grabbed would still move and delete it, and
  // the selection arrives from many places — placement, the properties list,
  // the 3D view, the assistant's highlight, duplication —, so the rule sits on
  // the state, not on each caller: an object selected on a faded or hidden
  // layer brings its layer back. A tool does the same for the layer it puts
  // its objects on.
  useEffect(() => {
    aprindeLayerele(layereleAlegerii(ales, plan));
  }, [ales]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const id = layerOfTool(unealta, piesa, aparat);
    if (id) aprindeLayerele([id]);
  }, [unealta, piesa, aparat]); // eslint-disable-line react-hooks/exhaustive-deps

  /** PUNE UN DOCUMENT PE MASĂ — la deschidere, sau când pagina aduce altul
   *  (alt nivel, soluția recitită după o salvare).
   *
   *  REPERUL CAMERELOR se ia AICI, pe geometria încă neatinsă — v.
   *  `reperulCamerelor`. Istoricul și selecția încep de la zero: un ⌘Z care ar
   *  întoarce documentul de dinainte de schimbarea nivelului ar pune pereții
   *  etajului 2 peste etajul 3. */
  const deschide = useCallback((d0: TronsonDoc, reper?: TronsonDoc) => {
    let d = d0;
    // `reper`: geometria NEATINSĂ, când `d` e o ciornă redeschisă — corpul
    // lăsat și reluat pe «Toate corpurile» (v. `plan/EditorNivel`). Reperul
    // se ia pe ce era în soluție, nu pe ce s-a mutat între timp.
    // CHEILE COTELOR STINSE, mutate pe socoteala nouă — o dată, aici, fiindcă
    // aici intră orice document pe masă (v. `model::cuCoteleStinseMutate`).
    d = cuCoteleStinseMutate(d);
    bazaRef.current = reperulCamerelor(reper ?? d);
    istoric.current = new Istoric();
    bazaGest.current = null;
    docRef.current = d;
    setDoc(d);
    setAles(null);
    setAlese([]);
    setPereteleAlese([]);
    setMobilaAlese([]);
    setGolurileAlese([]);
    redeseneaza((n) => n + 1);
  }, []);

  /** CALCUL MODULULUI, cerut la deschidere. Nu ține de document — e rând al
   *  lui, pe (proiect, cod), v. `viewer/tronson/calcStocare.ts`.
   *
   *  Vine de pe server, deci vine TÂRZIU: dacă între timp s-a deschis alt
   *  modul, răspunsul care sosește nu mai e al ecranului de acum și se aruncă.
   *  Fără paza asta, două deschideri repezi ar fi putut lăsa pe masă hârtia
   *  modulului dinainte. */
  useEffect(() => {
    let alecesta = true;
    setCalc(null);
    // A level's menu cannot place a paper, so there is none to ask for.
    if (fel === "nivel") return;
    void iaCalcul(projectId, code).then((c) => { if (alecesta) setCalc(c); });
    return () => { alecesta = false; };
  }, [projectId, code, fel]);

  /** Îl schimbă — pe ecran acum, pe server imediat sau după pauză, după cum e
   *  poză nouă ori numai calibrare (v. `calcStocare.ts`). Scris numai aici, ca
   *  să nu existe o a doua cale prin care ecranul și rândul se pot contrazice. */
  const schimbaCalcul = useCallback((c: Calc | null) => {
    setCalc(c);
    void puneCalcul(projectId, code, c);
  }, [projectId, code]);
  const calcRef = useRef<Calc | null>(null);
  calcRef.current = calc;
  const schimbaCalculRef = useRef(schimbaCalcul);
  schimbaCalculRef.current = schimbaCalcul;

  // ---- schimbări + istoric ----------------------------------------------- #
  //
  // DOUĂ FUNCȚII, NU UNA, și separarea e chiar ce face ⌘Z să însemne ceva.
  // `pornesteGest` reține documentul de DINAINTEA gestului, o dată; `schimba`
  // doar pune noua stare, de zeci de ori cât ține o tragere. Cu o singură
  // funcție care ar pune în istoric «la sfârșit», documentul reținut ar fi cel
  // deja mutat — și anularea ar restaura exact starea de care voiai să scapi.
  /** Documentul de la ÎNCEPUTUL gestului curent — față de el se măsoară cât
   *  s-au mutat zidurile, ca conturul să le urmeze (v.
   *  `model::conturUrmeazaZidurile`). Se pune la pornirea gestului și rămâne
   *  până la următorul: o mutare din taste (`aplica`) își are baza în
   *  documentul de dinaintea ei. */
  const bazaGest = useRef<TronsonDoc | null>(null);
  const pornesteGest = useCallback(() => {
    if (docRef.current) {
      istoric.current.pune(docRef.current);
      bazaGest.current = docRef.current;
    }
    redeseneaza((n) => n + 1);
  }, []);

  /** O schimbare de pe pânză. Cu `viu`, gestul a mutat ziduri și camerele se
   *  refac PE LOC — ariile și cotele se rescriu sub mână (v. antetul din
   *  `Panza`). Costă 10–14 ms pe modulul cel mai mare, măsurat, adică sub un
   *  cadru; gesturile care nu ating ziduri nu plătesc. */
  const schimba = useCallback((nou: TronsonDoc, viu?: boolean) => {
    const baza = bazaGest.current ?? nou;
    const d = viu
      ? cuCamereDerivate(conturulDupaDesen(baza, nou), bazaRef.current, baza)
      : nou;
    // `docRef` SE SCRIE ȘI AICI, din același motiv ca în `aplica`: un gest care
    // schimbă documentul ȘI se încheie în ACEEAȘI tură de JavaScript citea în
    // `terminaGest` documentul de dinaintea lui — și-l punea înapoi peste cel
    // nou. Cu mouse-ul mișcările și ridicarea degetului sunt ture diferite,
    // deci nu se vedea; conturul liber se închide însă dintr-un singur
    // eveniment (clic pe primul vârf sau Enter), iar camera desenată dispărea
    // în clipa în care era pusă. Zona de desfumare nu se putea desena DELOC —
    // «the zona de desfumare editing tool still doesn't finish on enter»
    // (15.10.2026), și nici pe clic nu se termina.
    docRef.current = d;
    setDoc(d);
  }, []);

  /** CAMERELE SE REFAC DIN PEREȚI după orice schimbare de geometrie.
   *
   *  Asta face și Homestyler (verificat pe înregistrare, 09.09.2026): tragi
   *  fațada în sus, zidurile prinse de capetele ei se lungesc, cele care intrau
   *  perpendicular rămân — iar camerele se recalculează după rețeaua rezultată.
   *  Acolo trei camere de 27, 14,7 și 34,8 m² au ieșit una singură de 128,9,
   *  fiindcă zidurile nu le mai despărțeau.
   *
   *  Fără pasul ăsta, camerele rămâneau poligoane de sine stătătoare care se
   *  potriveau cu zidurile doar din întâmplare — de-acolo veneau «camerele arată
   *  ciudat» și planul rupt. */
  /** ȘI CONTURUL URMEAZĂ ZIDURILE, înaintea camerelor (v.
   *  `model::conturUrmeazaZidurile`): o fațadă trasă în afară lărgește modulul,
   *  iar camerele se derivă apoi în conturul nou. `contur = true` = gestul a
   *  fost chiar pe contur — din mână sau din câmpurile de front și bandă —, și
   *  atunci conturul e ce a cerut omul, nu ce dictează zidurile. */
  const cuCamere = useCallback(
    (baza: TronsonDoc, d: TronsonDoc, contur = false) => cuCamereDerivate(
      contur ? d : conturulDupaDesen(baza, d), bazaRef.current, baza), []);

  /** ZIDURILE CU VOIE SĂ FIE CURBE — bifa «Perete curbat» din panou, pe id.
   *
   *  Stare de ECRAN, nu de document: documentul știe doar `bulge`, iar un zid
   *  drept cu bifa pusă e un zid drept. Bifa deschide câmpul de curbură și
   *  apucătoarea de pe desen; scoasă, zidul se face drept la loc. Butonul
   *  «Fă-l curb» din bara de context și unealta «Perete curb» au ieșit:
   *  curbura e o însușire a zidului ales, nu o unealtă. */
  const [curbPermis, setCurbPermis] = useState<Record<string, boolean>>({});

  /** Un gest de-al panoului: un singur pas de istoric, imediat. */
  const aplica = useCallback((f: (d: TronsonDoc) => TronsonDoc,
                              opt?: { contur?: boolean; faraCamere?: boolean }) => {
    const curent = docRef.current;
    if (!curent) return;
    istoric.current.pune(curent);
    // `faraCamere`: the change moved its rooms itself (`repair45::squareWalls`
    // moves room corners with the faces they sit on; a rebuild would re-derive
    // rooms the drafts do not draw as the space between walls).
    const nou = opt?.faraCamere ? f(curent) : cuCamere(curent, f(curent), opt?.contur);
    // `docRef` SE SCRIE AICI, nu doar la desenare. Două gesturi trimise în
    // aceeași tură de JavaScript — asistentul poate cere mai multe unelte
    // deodată — s-ar fi citit amândouă din documentul de dinaintea primului,
    // iar al doilea l-ar fi rescris pe primul. Cu mouse-ul nu se putea
    // întâmpla: între două gesturi ale mâinii React apucă să deseneze.
    docRef.current = nou;
    setDoc(nou);
    redeseneaza((n) => n + 1);
  }, [cuCamere]);

  /** Sfârșitul unui gest de pe pânză: acolo se refac camerele, o dată. */
  const terminaGest = useCallback((fel?: "contur") => {
    const curent = docRef.current;
    if (curent) setDoc(cuCamere(bazaGest.current ?? curent, curent, fel === "contur"));
    redeseneaza((n) => n + 1);
  }, [cuCamere]);

  const anuleaza = useCallback(() => {
    const curent = docRef.current;
    if (!curent) return;
    const d = istoric.current.anuleaza(curent);
    // V. nota din `aplica`: «anulează 3 pași» e un singur apel, deci ref-ul
    // trebuie să fie la zi înainte de al doilea.
    if (d) { docRef.current = d; setDoc(d); }
    // SELECȚIA RĂMÂNE, dacă obiectul mai există — cerut de arhitect
    // (21.10.2026): «când am un obiect selectat și fac "undo" aș vrea să rămână
    // obiectul selectat». Până acum orice pas de istoric o golea, deci după
    // fiecare ⌘Z obiectul trebuia ales din nou ca să poți încerca altceva.
    if (!d || !maiExista(d, alesRef.current)) alege(null);
    redeseneaza((n) => n + 1);
  }, []);

  const refa = useCallback(() => {
    const curent = docRef.current;
    if (!curent) return;
    const d = istoric.current.refa(curent);
    // V. nota din `aplica`: «anulează 3 pași» e un singur apel, deci ref-ul
    // trebuie să fie la zi înainte de al doilea.
    if (d) { docRef.current = d; setDoc(d); }
    // SELECȚIA RĂMÂNE, dacă obiectul mai există — cerut de arhitect
    // (21.10.2026): «când am un obiect selectat și fac "undo" aș vrea să rămână
    // obiectul selectat». Până acum orice pas de istoric o golea, deci după
    // fiecare ⌘Z obiectul trebuia ales din nou ca să poți încerca altceva.
    if (!d || !maiExista(d, alesRef.current)) alege(null);
    redeseneaza((n) => n + 1);
  }, []);

  /** ARATĂ CE S-A SCHIMBAT — v. `editor/aratare.ts`.
   *
   *  Se măsoară pe planurile DERIVATE, adică pe ce se vede, nu pe document. Cât
   *  ține arătarea, documentul e deja cel nou: ce se joacă pe ecran e drumul
   *  dintre ele, nu o stare intermediară a proiectului.
   *
   *  LA SFÂRȘIT RĂMÂNE ALES ce a fost atins, dacă e un singur lucru. Așa
   *  urmează firesc «și mai la stânga»: panoul din dreapta arată chiar obiectul
   *  despre care se vorbește, iar starea ecranului trimisă cu mesajul următor
   *  îl poartă cu ea (v. `Asistent::stareaEcranului`). O ștergere stinge
   *  selecția: indicii de după ea sunt alții, iar o selecție rămasă pe un
   *  număr ar fi arătat spre alt obiect decât cel de dinainte. */
  const ceasAratarii = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nrAratarii = useRef(0);
  const arata = useCallback((vechiDoc: TronsonDoc, nouDoc: TronsonDoc) => {
    const pv = deriva(vechiDoc);
    const pn = deriva(nouDoc);
    const tinte = diferenta(pv, pn);
    if (!tinte.length) return;
    // What the assistant changed is shown even on a faded or hidden layer:
    // its reply says it was done, so the canvas must show it.
    aprindeLayerele(tinte.filter((x) => x.fel !== "sters")
      .flatMap((x) => layereleAlegerii(x.ales, pn)));
    const program = programeaza(tinte);
    nrAratarii.current += 1;
    setAratare({ nr: nrAratarii.current, tinte, vechi: pv, program });
    if (ceasAratarii.current) clearTimeout(ceasAratarii.current);
    ceasAratarii.current = setTimeout(() => {
      setAratare(null);
      const ramase = tinte.filter((t) => t.fel !== "sters");
      if (ramase.length === 1) alege(ramase[0].ales);
      else if (ramase.length !== tinte.length) alege(null);
    }, program.total);
  }, [alege, aprindeLayerele]); // eslint-disable-line react-hooks/exhaustive-deps
  const arataRef = useRef(arata);
  arataRef.current = arata;
  useEffect(() => () => {
    if (ceasAratarii.current) clearTimeout(ceasAratarii.current);
  }, []);

  /** Rotește ce e ales, în jurul lui — ACEEAȘI POARTĂ CA BUTONUL din bara
   *  selecției (`model::rotesteObiectul`), nu o a doua socoteală.
   *
   *  Două liste de feluri scrise în două locuri se despart: una ar învăța
   *  zidul și nucleul, cealaltă nu. Ce NU se rotește spune `intoarcerile`, tot
   *  dintr-un loc. */
  /** OBIECTUL PE CARE CAD FAPTELE ÎNTREGI, pornind de la ce e aprins.
   *
   *  O parte de scară — o rampă, un podest — se alege separat ca să i se poată
   *  spune treptele ori adâncimea. Dar DUBLAREA, OGLINDIREA și împinsul din
   *  săgeți sunt ale SCĂRII: o rampă dublată singură n-ar fi o scară, ar fi o
   *  bucată de drum. Cerut de arhitect (21.10.2026): «scările ar trebui să
   *  poată fi dublate și oglindite ca obiectele», «scările să poată fi mutate
   *  cu săgețile câte 5 cm».
   *
   *  E aceeași hotărâre ca la mutarea cu mâna, unde apucarea oricărei părți
   *  mută scara întreagă (v. `model::mutaScaraIntreaga`). */
  const caIntreg = useCallback((a: Ales): Ales =>
    (a?.fel === "scaraParte" ? { fel: "mobila", i: a.i } : a), []);

  const roteste = useCallback((grade: number) => {
    const a = caIntreg(alesRef.current);
    if (a) aplica((d) => rotesteObiectul(d, a, grade));
  }, [aplica, caIntreg]);

  /** DUBLEAZĂ CE E ALES ȘI TRECE PE COPIE — una și aceeași pentru butonul din
   *  bară și pentru `CO`. `dubleaza` întoarce și documentul, și alegerea nouă:
   *  cine copiază vrea copia în mână, nu originalul. */
  /** TOT CE E APRINS, ca listă de obiecte alese.
   *
   *  O SELECȚIE MULTIPLĂ E OMOGENĂ prin construcție: fiecare ramură de adunare
   *  golește celelalte două liste (v. `alege`), deci cel mult una e plină. Iar
   *  un CLIC SIMPLU își pune obiectul în lista felului lui, tot acolo — de-aia
   *  lista ajunge și pentru unul singur, și faptele de grup n-au nevoie de două
   *  drumuri. E chiar principiul scris deja pentru ziduri: «un zid ales simplu
   *  rămâne singur în listă, ca trasul lui să treacă pe aceeași cale ca al mai
   *  multora».
   *
   *  FELURILE FĂRĂ LISTĂ rămân pe `ales`: un gol, un aparat, o axă, o parte de
   *  scară nu se adună cu Shift, deci n-au ce grup să formeze. */

  const aleseleToate = useCallback((): Ales[] => {
    // REUNIUNEA, nu prima listă nevidă: de când ⇧ poate aduna ziduri ȘI piese
    // (22.10.2026), «ce e ales» e tot ce arde pe ecran. Ștergerea, dublarea și
    // rangul citesc tocmai lista asta, iar întoarsă pe jumătate ar fi lăsat în
    // urmă exact bucățile pe care omul le vedea alese.
    const tot: Ales[] = [
      ...pereteleAleseRef.current.map((i) => ({ fel: "perete", i }) as Ales),
      ...mobilaAleseRef.current.map((i) => ({ fel: "mobila", i }) as Ales),
      ...golurileAleseRef.current.map(
        (g) => ({ fel: "gol", fel2: g.fel2, i: g.i }) as Ales),
    ];
    if (tot.length) return tot;
    // Camerele rămân singure în listă: ele nu se mută și nu se adună cu
    // celelalte (v. `alege`).
    if (aleseRef.current.length) {
      return aleseRef.current.map((i) => ({ fel: "camera", i }) as Ales);
    }
    const a = caIntreg(alesRef.current);
    return a ? [a] : [];
  }, [caIntreg]);

  /** APRINDE DEODATĂ O LISTĂ ÎNTREAGĂ — chenarul de ales (v. `Panza`).
   *
   *  NU SE POATE FACE CU `alege(a, true)` CHEMAT ÎN BUCLĂ, și asta e miezul:
   *  adunarea citește `mobilaAleseRef`/`pereteleAleseRef`, iar refurile se
   *  scriu la RANDARE. Zece chemări într-o singură tură citesc toate aceeași
   *  listă veche, deci ultima le șterge pe celelalte — măsurat: dintr-un
   *  chenar peste două piese rămânea aprinsă una.
   *
   *  ZIDURI, PIESE ȘI GOLURI DEODATĂ: listele sunt deosebite, dar nu se sting
   *  una pe alta — `aleseleToate` le reunește de când ⇧ adună ziduri ȘI piese. */
  const alegeLista = useCallback((lista: readonly Ales[]) => {
    const pereti: number[] = [];
    const piese: number[] = [];
    const goluri: { fel2: "doors" | "windows"; i: number }[] = [];
    for (const a of lista) {
      if (a?.fel === "perete") pereti.push(a.i);
      else if (a?.fel === "mobila") piese.push(a.i);
      else if (a?.fel === "gol") goluri.push({ fel2: a.fel2, i: a.i });
    }
    setAlese([]);
    setPereteleAlese(pereti);
    setMobilaAlese(piese);
    setGolurileAlese(goluri);
    // O selecție nouă încheie «din start»-ul unei copii, ca `alege`.
    setDublateAcum((v) => (v ? null : v));
    // `ales` e cel din urmă aprins: panoul din dreapta arată ceva, iar bara de
    // context se deschide. Ordinea e a listei primite.
    setAles(piese.length ? { fel: "mobila", i: piese[piese.length - 1] }
      : pereti.length ? { fel: "perete", i: pereti[pereti.length - 1] }
        : goluri.length
          ? { fel: "gol", fel2: goluri[0].fel2, i: goluri[0].i } : null);
  }, []);

  const dubleazaAles = useCallback(() => {
    // CÂTE UNA PE ANSAMBLU: `dubleaza` pe o piesă dintr-un grup copiază grupul
    // ÎNTREG (v. `model::dubleaza`), iar de când un clic aprinde tot ansamblul
    // selecția are toate piesele lui — chemată pe fiecare, dublarea făcea câte
    // un ansamblu nou de fiecare piesă. Cerut de arhitect (21.10.2026): «când
    // selectez o insulă și trei scaune și apăs dublează creează 3 grupări în
    // plus în loc de 1».
    const lista = cateUnaPeGrup(docRef.current ?? ({} as TronsonDoc),
                                aleseleToate());
    if (!lista.length) return;
    // CRESCĂTOR, și n-are de ce să fie altfel: `dubleaza` PUNE copia la capătul
    // listei din document, deci indicii celor încă nedublate nu se mișcă. (La
    // ștergere e pe dos — acolo ordinea descrescătoare e obligatorie, v.
    // `stergeAlesele`.)
    //
    // PRIN `aplica`, nu cu `setDoc` de-a dreptul: el pune documentul în istoric
    // și reface camerele. Scrisă pe `setDoc`, dublarea nu intra în istoric
    // deloc, deci un ⌘Z de după ea sărea peste gestul de dinainte; pe o dublare
    // de cinci piese ar fi fost și mai rău, fiindcă cele cinci ar fi plecat
    // într-un singur pas nescris.
    const copii: number[] = [];
    let ultim: Ales = null;
    aplica((d) => {
      let r = d;
      for (const a of lista) {
        // TOATE COPIILE, nu doar cea aleasă: un ansamblu dublat pune în
        // document atâtea piese câte are, iar `dubleaza` întoarce numai pe cea
        // apăsată. Luate doar pe ea, aprinderea ar fi arătat o piesă dintr-un
        // ansamblu de patru — adică iar un desen care minte despre ce ține
        // omul în mână. Copiile se pun la CAPĂTUL listei, deci sunt tocmai
        // indicii de după lungimea de dinainte.
        const inainte = a?.fel === "mobila"
          ? (r.plan?.furniture?.length ?? 0) : -1;
        const x = dubleaza(r, a);
        r = x.doc;
        if (inainte >= 0) {
          for (let k = inainte; k < (r.plan?.furniture?.length ?? 0); k++)
            copii.push(k);
        } else if (x.ales && "i" in x.ales) copii.push(x.ales.i);
        ultim = x.ales ?? ultim;
      }
      return r;
    });
    // COPIILE RĂMÂN ÎN MÂNĂ, ca la una singură («cine copiază vrea copia în
    // mână, nu originalul»): selecția trece pe ele, nu pe originale — altfel
    // lista ar fi arătat originalele cu `ales` pe o copie, adică două lucruri
    // diferite aprinse în același timp.
    const fel = lista[0] && "fel" in lista[0] ? lista[0].fel : null;
    if (fel === "perete") {
      setPereteleAlese(copii);
      setDublateAcum({ fel: "perete", indici: copii });
    } else if (fel === "camera") setAlese(copii);
    else if (fel === "mobila") {
      setMobilaAlese(copii);
      // ȘI MOBILIERUL E «PROASPĂT», nu doar zidurile: o copie trasă prima oară
      // nu se agață de nimic — «obiectele rezultate din dublează nu ar trebui
      // să se agațe de nimic din obiectele existente în prima fază»
      // (26.10.2026).
      setDublateAcum({ fel: "mobila", indici: copii });
    }
    setAles(ultim);
  }, [aplica, aleseleToate]);

  /** Oglindește ce e ales, pe axa cerută — perechea de mai sus. */
  const oglindeste = useCallback((axa: "h" | "v") => {
    const a = caIntreg(alesRef.current);
    if (a) aplica((d) => oglindesteObiectul(d, a, axa));
  }, [aplica, caIntreg]);

  /** BAGHETA 🪄 — v. `design/auto.ts`.
   *
   *  ÎNTREABĂ ÎNAINTE când e ceva de pierdut. Ea rescrie mobilierul și
   *  instalația de la zero, deci pe un modul deja amenajat de mână ar șterge
   *  munca de o oră fără să spună nimic. Cu mobilierul deja pus, se poate cere
   *  doar instalația peste el — și aia e alegerea pe care o oferă întrebarea.
   */
  const bagheta = useCallback((ce: "mobilier" | "electric") => {
    const d = docRef.current;
    if (!d || !plan) return;
    const cuMobilier = ce === "mobilier";
    const cuElectrice = ce === "electric";
    // ÎNTREABĂ DOAR DESPRE CE REScrie. Jumătatea cealaltă rămâne neatinsă, deci
    // n-are de ce să intre în întrebare — asta era și reproșul vechiului dialog
    // cu «OK / Anulează»: alegerea dintre mobilier și instalație stătea ascunsă
    // într-o întrebare, în loc să fie două butoane.
    const cate = cuMobilier
      ? (d.plan?.furniture ?? []).length : (d.plan?.electrical ?? []).length;
    if (cate > 0 && !window.confirm(tr(
      cuMobilier ? "editor.bagheta_intrebare_mobilier"
        : "editor.bagheta_intrebare_electric",
      { piese: (d.plan?.furniture ?? []).length,
        aparate: (d.plan?.electrical ?? []).length }))) return;
    const r = mobileazaAutomat(d, plan, { cuMobilier, cuElectrice });
    istoric.current.pune(d);
    setDoc(r.doc);
    alege(null);
    redeseneaza((n) => n + 1);
    setVedere("2d");
    setMod("design");
    const sarite = r.sarite.length
      ? trn("editor.bagheta_sarite#n", r.sarite.length,
            { motive: r.sarite.map((x) => x.de_ce).join("; ") })
      : "";
    setRaportBagheta(tr("editor.bagheta_raport", {
      mobilier: cuMobilier ? tr("editor.bagheta_mobilier", { piese: r.piese }) : "",
      aparate: r.aparate,
      sarite,
    }));
  }, [plan]);

  // Raportul baghetei se stinge la prima atingere a desenului: e o confirmare,
  // nu o stare a modulului.
  useEffect(() => { setRaportBagheta(null); }, [ales, unealta]);

  // ---- comenzile din două litere, ca în Revit -------------------------- #
  //
  // DE CE DOUĂ LITERE. Lista de la birou e a Revit-ului, iar arhitecții o au în
  // degete: `WA` perete, `DR` ușă, `WN` fereastră. Se tastează una după alta,
  // fără Enter — exact ca acolo. Cerut 04.10.2026, cu lista lor în față.
  //
  // NUMAI PE TRONSON. Pe nivelul de soluție tastele rămân cele dintotdeauna
  // («the keys only change for tronsoane, for the rest of the tabs they stay
  // the same»), de unde și `fel` în opțiunile hook-ului.
  //
  // CE PLEACĂ DE PE TRONSON: `W`/`A`/`S`/`D` nu mai împing selecția, fiindcă
  // `WA` și `DR` încep cu ele, iar o literă nu poate și să facă ceva imediat,
  // și să aștepte o a doua. Săgețile împing mai departe, neatinse — erau deja
  // acolo. La fel `R`, `H` și `V`: le iau locul `RO`, `MM` și `DM`.
  const literaDeAsteptare = useRef<{ ch: string; la: number } | null>(null);
  /** Cât se așteaptă a doua literă. O secundă și jumătate: cât îi ia unei mâini
   *  care știe comanda, și destul de scurt cât să nu lege două apăsări
   *  nelegate. */

  // ---- taste ------------------------------------------------------------- #
  useEffect(() => {
    const la = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t?.tagName === "INPUT" || t?.tagName === "SELECT") return;
      const meta = e.metaKey || e.ctrlKey;

      // Z SINGUR — ÎNAPOI LA TOT MODULUL.
      //
      // Era `ZA`, un acord de două litere ca în Revit; arhitectul a cerut o
      // singură tastă: «can you make a keyboard shortcut for the Înapoi la tot
      // modulul on the Z key. Just by pressing Z» (20.10.2026). Stă ÎNAINTEA
      // acordurilor fiindcă altfel Z ar fi fost ținut în așteptare ca prima
      // literă a lui `ZA`, iar reîncadrarea ar fi venit abia după a doua tastă
      // — adică niciodată, dacă nu apeși nimic. `ZA` a și ieșit din tabel: cu
      // Z care se aprinde singur, el nu s-ar mai fi putut compune.
      //
      // ⌘Z rămâne anularea: `meta` îl scoate de-aici.
      // `Z` E AL PÂNZEI, nu al mesei: ea îl duce pe OBIECTUL ALES, iar fără
      // nimic ales încadrează tot planul (v. `Panza::incadreazaAles`) — adică
      // exact ce făcea rândul de aici, plus cazul care lipsea.
      //
      // ERA O A DOUA LEGĂTURĂ PE ACEEAȘI TASTĂ, și ea e defectul pe care
      // arhitectul l-a arătat de trei ori (22.10.2026, «zoom-ul pe obiecte e
      // tot cu probleme»): cele două ascultătoare se declanșau amândouă, pânza
      // se apropia pe obiect, iar `setIncadrari` o trăgea înapoi pe tot planul
      // în aceeași apăsare. De-aici și prima descriere, «se vede o secundă și
      // dispare după» — nu era nici cutia, nici focusul, era reîncadrarea de
      // aici care bătea apropierea.
      //
      // TASTA SE CONSUMĂ TOT AICI, fiindcă altfel `z` ar deveni prima literă a
      // unei comenzi din două (v. mai jos) și s-ar aprinde abia la a doua
      // tastă — adică niciodată. Ce s-a scos e DOAR reîncadrarea.
      if (fel === "tronson" && !meta && (e.key === "z" || e.key === "Z")) {
        literaDeAsteptare.current = null;
        return;
      }
      // COMENZILE DIN DOUĂ LITERE, înaintea tuturor celorlalte: ele mănâncă
      // literele pe care le-ar fi luat tastele vechi.
      if (fel === "tronson" && !meta && /^[a-zA-Z]$/.test(e.key)) {
        const ch = e.key.toUpperCase();
        const prim = literaDeAsteptare.current;
        const acum = Date.now();
        const cod = prim && acum - prim.la <= RASTIMP_COMANDA_MS ? prim.ch + ch : null;
        const fapta = cod ? COMENZI_REVIT[cod] : undefined;
        if (fapta) {
          literaDeAsteptare.current = null;
          e.preventDefault();
          fapta({ e, setUnealta, roteste, oglindeste, ales,
                 alesele: aleseleToate(), potScrie, aplica,
                  alege, reincadreaza: () => setIncadrari((n) => n + 1),
                  setStransa, dubleazaAles });
          return;
        }
        // Litera asta poate fi începutul unei comenzi? Atunci se ține și se
        // așteaptă; altfel se uită, și tasta merge mai departe pe drumul vechi.
        literaDeAsteptare.current = INCEPUTURI.has(ch) ? { ch, la: acum } : null;
        if (INCEPUTURI.has(ch)) { e.preventDefault(); return; }
      }
      // ANULAREA ȘI REFACEREA SUNT DOAR AICI, de când butoanele au plecat din
      // bara de sus. Deci trebuie să răspundă la tot ce apasă lumea pentru ele:
      // ⌘Z / ⇧⌘Z pe Mac, Ctrl+Z / Ctrl+⇧+Z pe Windows — și Ctrl+Y, care e
      // refacerea din Windows și pe care o apasă oricine vine dintr-un CAD.
      if (meta && e.key.toLowerCase() === "z") {
        e.preventDefault();
        (e.shiftKey ? refa : anuleaza)();
      } else if (meta && e.key.toLowerCase() === "y") {
        e.preventDefault();
        refa();
      } else if (!meta && (e.key === "r" || e.key === "R")) {
        // R ROTEȘTE CE E ALES, cu 90°; cu Shift, înapoi. Un sfert de tur e ce
        // se cere de zece ori din unsprezece — o piesă se întoarce spre alt
        // perete, nu la un unghi anume. Pentru unghiuri fine sunt «,» și «.»,
        // iar pentru unul exact e câmpul din panou. Pe ce nu se rotește —
        // camera, conturul, un gol — nu face nimic, ca și butonul stins din
        // bară (v. `model::intoarcerile`).
        if (ales) {
          e.preventDefault();
          roteste(e.shiftKey ? -90 : 90);
        }
      } else if (!meta && (e.key === "," || e.key === ".")) {
        if (ales) {
          e.preventDefault();
          roteste(e.key === "," ? -15 : 15);
        }
      } else if (!meta && "hHvV".includes(e.key) && ales) {
        // H ȘI V OGLINDESC CE E ALES, pe orizontală și pe verticală — aceleași
        // două comenzi ca butoanele din bară, prin aceeași poartă
        // (`model::oglindesteObiectul`).
        //
        // PE TOT CE SE POATE OGLINDI, nu doar pe mobilier și aparate: o
        // oglindă are ce face și pe un zid (își duce golurile la celălalt capăt,
        // își răstoarnă straturile), și pe o ușă (sensul ei), și pe nucleu
        // (scara de partea cealaltă a puțurilor). Ce n-are — camera, conturul,
        // fereastra — nu face nimic, ca și butonul stins.
        if (potScrie) {
          e.preventDefault();
          oglindeste(e.key === "h" || e.key === "H" ? "h" : "v");
        }
      } else if (!meta && (/^[wasd]$/i.test(e.key)
                           || /^Arrow(Left|Right|Up|Down)$/.test(e.key))) {
        // W/A/S/D ȘI SĂGEȚILE ÎMPING CE E ALES, din 5 în 5 cm.
        //
        // Cerut de Stefan: «când am selectat ceva, wasd să-l miște un pic», iar
        // apoi «adaugă și pe săgeți să facă la fel». Cât ține selecția, cele
        // patru direcții sunt ale obiectului, pe orice tastă s-ar apăsa: pânza
        // nu se mai plimbă cu ele (v. `Panza2D::obiectAles`), fiindcă altfel
        // obiectul ar pleca la stânga în timp ce planul fuge la dreapta pe sub
        // el. Fără nimic ales, toate patru panoramează mai departe, ca până
        // acum, iar panoramarea rămâne la îndemână și cu ceva ales — cu
        // mijlocul, cu dreapta sau cu spațiul apăsat.
        //
        // Shift + săgeată face același lucru: e felul în care se mișcau
        // săgețile înainte să fie ale obiectului de-a dreptul, și nu e nimic de
        // câștigat dintr-o combinație care acum ar da greș.
        //
        // Cinci centimetri: pasul la care se lipesc oricum toate mutările din
        // editor, deci o bucată împinsă din taste cade pe aceleași cifre ca una
        // trasă cu mâna. Pentru o aliniere exactă e câmpul din panou.
        //
        // PERETELE SE MUTĂ SINGUR, fără linia lui: cine ține un zid selectat și
        // apasă o săgeată vrea bucata aia acolo, nu tot zidul împins — v.
        // `mutaPereteleSingur` și cazul din care a ieșit.
        //
        // CAMERA NU E ÎN LISTĂ: nu se mută deloc, nici din taste, nici cu mâna
        // — v. antetul lui `editor/Panza`. Cu ea aleasă, literele rămân ale
        // pânzei și panoramează, ca și cum n-ar fi nimic ales (v.
        // `Panza::obiectAles`): niște taste care nu fac nimic sunt mai rele
        // decât niște taste care fac ce făceau.
        if (ales && ales.fel !== "camera" && potScrie) {
          e.preventDefault();
          // ⇧ + SĂGEATĂ ÎNTOARCE, săgeata singură MUTĂ.
          //
          // Cerut pentru zidul abia pus: «switch it's rotation from the arrow
          // keys» (19.10.2026). Săgețile singure erau deja luate — ele împing
          // obiectul ales din 5 în 5 cm, pe ORICARE din cele două axe, deci
          // mutarea liberă din taste exista; ce lipsea era întoarcerea.
          // Stânga/dreapta rotesc, ca `RO`, fiindcă aia e mâna pe care o
          // caută degetul; sus/jos rămân mutare, ca să nu dispară.
          if (e.shiftKey && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
            roteste(e.key === "ArrowLeft" ? -90 : 90);
            return;
          }
          const P = 0.05;
          const t = e.key.length === 1 ? e.key.toLowerCase() : e.key;
          const dep: [number, number] =
            t === "ArrowLeft" || t === "a" ? [-P, 0]
              : t === "ArrowRight" || t === "d" ? [P, 0]
                : t === "ArrowUp" || t === "w" ? [0, -P] : [0, P];
          aplica((d) => (
            // UN GOL SE ÎMPINGE ȘI EL DIN SĂGEȚI, dar rămâne pe un zid:
            // `mutaGolulLiber` îl așază pe peretele de sub noua poziție și
            // întoarce documentul neatins dacă acolo nu e niciunul.
            ales.fel === "gol"
              ? mutaGolulLiber(d, ales.fel2, ales.i,
                               [(d.plan?.[ales.fel2]?.[ales.i]?.x.a ?? 0) + dep[0],
                                (d.plan?.[ales.fel2]?.[ales.i]?.y.a ?? 0) + dep[1]])
              // TOȚI PEREȚII ALEȘI, nu doar ultimul apăsat. Arătat de arhitect
              // (22.10.2026): «dacă selectez mai mulți pereți și încerc să-i mut
              // folosind săgețile se mută doar ultimul selectat» — `ales` ține
              // minte un singur zid, iar restul selecției trăiește în
              // `pereteleAlese`, exact ca mobila de mai jos, care se mută de mult
              // întreagă. `grup` e chiar poarta pe care o cere `mutaPereteleSingur`
              // pentru mai multe bucăți: deplasarea se aplică O DATĂ pe tot grupul,
              // iar chemată pe rând pentru fiecare zid ar fi mutat de mai multe ori
              // foile lipite între ele (v. nota lui, și M1/W17 tras 0,30 care se
              // ducea 0,90).
              // ȘI PIESELE DIN ACEEAȘI SELECȚIE, când ⇧ a adunat de amândouă
              // felurile (22.10.2026) — săgețile fac ce face mâna, v. `Panza`.
              // Piesele întâi, zidurile peste ele: `mutaPereteleSingur` lucrează
              // pe documentul în care piesele au ajuns deja unde trebuie.
              : ales.fel === "perete"
                ? mutaPereteleSingur(
                  mobilaAleseRef.current.length
                    ? mutaMobilaMulte(d, mobilaAleseRef.current, dep) : d,
                  ales.i, dep, { grup: pereteleAleseRef.current,
                                 dezlipeste: true })
              // TOATĂ SELECȚIA, cu săgețile ca și cu mâna: lista are și
              // piesa aleasă singură (v. `aleseleToate`).
              : ales.fel === "mobila"
                ? (pereteleAleseRef.current.length
                  ? mutaPereteleSingur(
                    mutaMobilaMulte(d, mobilaAleseRef.current, dep),
                    pereteleAleseRef.current[0], dep,
                    { grup: pereteleAleseRef.current, dezlipeste: true })
                  : mutaMobilaMulte(d, mobilaAleseRef.current, dep))
              // O PARTE DE SCARĂ împinge scara întreagă, ca sub mână.
              : ales.fel === "scaraParte"
                ? mutaMobilaMulte(d, [ales.i], dep)
                : ales.fel === "aparat" ? mutaAparat(d, ales.i, dep)
                  : ales.fel === "nucleuParte"
                    ? mutaParteaNucleului(d, ales.i, dep)
                    : ales.fel === "nucleu" ? mutaNucleul(d, dep)
                      : d));
        }
      } else if (e.key === "Delete" || e.key === "Backspace") {
        // TOATĂ SELECȚIA, ca butonul «Șterge» și ca tasta `DE`. Aici scria
        // `sterge(d, ales)` — unul singur —, deci butonul lua cinci piese și
        // tasta una: «dacă am mai multe obiecte selectate și apăs Șterge le
        // șterge pe toate dar dacă folosesc tasta delete șterge doar unul»
        // (21.10.2026). Al treilea loc în care o faptă își avea a doua
        // scriere, rămasă în urmă.
        const toate = aleseleToate();
        if (toate.length) {
          e.preventDefault();
          aplica((d) => stergeAlesele(d, toate));
          alege(null);
        }
      } else if (e.key === "Escape") {
        // ESC ÎNSEAMNĂ «înapoi la săgeată». O uneltă de pus rămâne activă până
        // aici — cine pune o ușă pune de obicei mai multe, iar întoarcerea la
        // săgeată după fiecare ar cere un drum la paletă între ele.
        //
        // Când e deja săgeata, aceeași tastă golește selecția: e ordinea în
        // care se apasă oricum, și n-ai cum să ai nevoie de amândouă deodată.
        setUnealta((u) => {
          if (u !== "sageata") return "sageata";
          alege(null);
          return u;
        });
      }
    };
    window.addEventListener("keydown", la);
    return () => window.removeEventListener("keydown", la);
  }, [ales, potScrie, anuleaza, refa, aplica, roteste, oglindeste]);

  return {
    doc, docRef, plan, deschide,
    ales, alese, pereteleAlese, mobilaAlese, golurileAlese,
    alesRef, aleseRef, pereteleAleseRef, mobilaAleseRef, golurileAleseRef, alege,
    unealta, setUnealta, piesa, setPiesa, stransa, setStransa,
    panouStrans, setPanouStrans, dublateAcum,
    incadrari, reincadreaza: () => setIncadrari((n) => n + 1), dubleazaAles,
    alegeLista,
    aleseleToate,
    vedere, setVedere, mod, setMod, aparat, setAparat, finisaj, setFinisaj,
    raportBagheta, setRaportBagheta, aratare,
    calc, calcRef, schimbaCalcul, schimbaCalculRef,
    araAxe, setAraAxe, araEtichete, setAraEtichete,
    layere, schimbaLayerul, layereDeschise, setLayereDeschise,
    araCote, setAraCote, araApartamente, setAraApartamente,
    istoric, redeseneaza, bazaGest,
    pornesteGest, schimba, aplica, terminaGest, anuleaza, refa, arata,
    curbPermis, setCurbPermis, roteste, oglindeste, bagheta,
  };
}

export type Masa = ReturnType<typeof useMasa>;

/** COMUTATOARELE DESENULUI — axe, etichete, layerele, hârtia de calc.
 *
 *  Stau în bara de sus a paginii, nu în corpul mesei, fiindcă bara e a paginii;
 *  dar sunt ale mesei, deci le desenează ea: un comutator nou apare în
 *  amândouă paginile deodată. */
export function ComutatoareMasa({ m, laEroare, fel = "tronson" }: {
  m: Masa; laEroare: (mesaj: string) => void; fel?: FelMasa;
}) {
  const t = useT();
  const { araAxe, setAraAxe, araEtichete, setAraEtichete,
          araCote, setAraCote, araApartamente, setAraApartamente,
          calc, schimbaCalcul, reincadreaza, layere, layereDeschise, setLayereDeschise } = m;
  const ascunse = LAYER_IDS.filter((id) => !editable(layere[id])).length;
  const alegeCalcul = useRef<HTMLInputElement | null>(null);
  const setEroare = laEroare;
  // PE NIVELUL UNEI SOLUȚII, PATRU COMUTATOARE, CU NUMELE SCRIS. Trei
  // filtre (Stefan, 23.09.2026: «vreau totuși câteva filtre, cel de cote,
  // etichete, și cel de colorarea apartamentelor, atât») și AXELE, cerute
  // după: «adaugă o capabilitate de axe, similar cu cele din paginile de
  // fundare, ca să poți trage de un ax». Axele sunt aceleași ca pe un modul —
  // citite din pereți (`axele`), trase de disc (`mutaAxul`). Pardoselile și
  // hârtia de calc rămân ale celui care desenează un modul.
  if (fel === "nivel") {
    const comutatoare = [
      [araAxe, setAraAxe, "axe", "editor.ascunde_axe", "editor.arata_axe", "nivel.filtru.axe"],
      [araCote, setAraCote, "cote", "editor.ascunde_cote", "editor.arata_cote",
       "viz.filtru.cote"],
      [araEtichete, setAraEtichete, "etichete", "editor.ascunde_etichete",
       "editor.arata_etichete", "viz.filtru.camere"],
      [araApartamente, setAraApartamente, "apartamente", "editor.ascunde_apartamente",
       "editor.arata_apartamente", "viz.filtru.apartamente"],
    ] as const;
    return (
      <div className="meniu-comutatoare">
        {comutatoare.map(([pornit, pune, icoana, stinge, aprinde, nume]) => (
          <button key={icoana} className={`meniu-comutator${pornit ? " on" : ""}`}
                  aria-pressed={pornit}
                  onClick={() => pune((a) => !a)}
                  title={t(pornit ? stinge : aprinde)}>
            <Icoana nume={icoana} marime={15} />
            <span>{t(nume)}</span>
          </button>
        ))}
      </div>
    );
  }
  return (
    <>
          {/* AXELE DE TRASARE, cu comutator — și stinse implicit.
              Cerute anume așa: «să ai grijă să fie ușor ca UX, să nu încurce
              lizibilitatea schemei». Un caroiaj permanent peste un plan cu 21
              de camere ar fi exact ce nu trebuie; aprins cât ține trasarea, e
              gestul de pe planșă. */}
          {/* ÎNAPOI LA TOT MODULUL. Reîncadrarea exista de mult, dar numai pe
              tastele `ZA` — adică pentru cine le știe. Cerut ca buton:
              «create a button for "Back to view" or smth that takes you back
              to the overall view of the whole tronson» (19.10.2026).
              Stă primul între comutatoarele vederii, fiindcă e singurul care
              nu schimbă CE se desenează, ci unde te uiți. */}
          <button className="ob-ed-icon" onClick={() => reincadreaza()}
                  title={t("editor.incadreaza")}>
            <Icoana nume="incadreaza" />
          </button>
          <button className={`ob-ed-icon${araAxe ? " on" : ""}`}
                  onClick={() => setAraAxe((a) => !a)}
                  title={t(araAxe ? "editor.ascunde_axe" : "editor.arata_axe")}>
            <Icoana nume="axe" />
          </button>
          {/* NUMELE ȘI ARIILE, care se pot da la o parte.
              Cerute anume: scrisul din mijlocul camerei «nu e neapărat bun
              vizual». S-a făcut în trei: acum intră în cameră (v.
              `Desen`, cadrul dinăuntru), se rupe pe două rânduri când nu
              încape pe unul — și se poate stinge de aici, ca orice adnotare.
              Cotele de pe laturi rămân aprinse: alea sunt măsuri. */}
          <button className={`ob-ed-icon${araEtichete ? " on" : ""}`}
                  onClick={() => setAraEtichete((a) => !a)}
                  title={t(araEtichete
                    ? "editor.ascunde_etichete" : "editor.arata_etichete")}>
            <Icoana nume="etichete" />
          </button>
          {/* THE LAYERS PANEL (sheet rows 97–98): it replaces the floors and
              electrics buttons. A dot says some layer is hidden or faded, so
              a plan that looks emptier than its document says why. */}
          <button className={`ob-ed-icon${layereDeschise ? " on" : ""}${ascunse ? " semn" : ""}`}
                  aria-pressed={layereDeschise}
                  onClick={() => setLayereDeschise((v) => !v)}
                  title={layereDeschise ? t("editor.layere.inchide")
                    : ascunse ? trn("editor.layere.ascunse#n", ascunse)
                    : t("editor.layere.deschide")}>
            <Icoana nume="layere" />
          </button>
          {/* HÂRTIA DE CALC, pusă cu mâna. Nu se pune numai la naștere: cine a
              desenat jumătate de modul și abia acum a primit planșa de la
              arhitect o pune acum, peste ce are. Fără foaie, butonul deschide
              alegerea fișierului; cu foaie, o stinge și o aprinde, ca bara ei
              de pe planșă — două locuri pentru același comutator, fiindcă
              mâna îl caută lângă celelalte straturi, iar ochiul lângă foaie. */}
          <input ref={alegeCalcul} type="file" accept="image/*"
                 style={{ display: "none" }}
                 onChange={(e) => {
                   const f = e.target.files?.[0];
                   e.target.value = "";
                   if (f) {
                     void imagineDinFisier(f)
                       .then((i) => schimbaCalcul(
                         calcNou(i.url, i.latime, i.inaltime)))
                       .catch(() => setEroare(t("editor.calc_esuat")));
                   }
                 }} />
          <button className={`ob-ed-icon${calc && !calc.ascuns ? " on" : ""}`}
                  onClick={() => {
                    if (!calc) alegeCalcul.current?.click();
                    else schimbaCalcul({ ...calc, ascuns: !calc.ascuns });
                  }}
                  title={t(!calc ? "calc.pune"
                    : calc.ascuns ? "calc.arata" : "calc.ascunde")}>
            <Icoana nume="calc" />
          </button>
    </>
  );
}

/** Ce spune panoul din dreapta despre tronsonul întreg — doar pe `tronson`. */
export type EticheteMasa = {
  etichete: string[];
  posibile: Eticheta[];
  lucrez: boolean;
  laSchimbare?: (etichete: string[]) => void;
};

/** CORPUL MESEI: raportul baghetei, paleta, pânza sau macheta, bara selecției,
 *  proprietățile. */
export function MasaDeLucru({ m, doarCitire, fel = "tronson", etichete,
                              despreNivel, culoriApartamente, colt, editatLa,
                              vecini, laVecin, cheieIncadrare, cadruTot }: {
  m: Masa;
  doarCitire: boolean;
  fel?: FelMasa;
  etichete?: EticheteMasa;
  /** Ce scrie panoul din dreapta când nu e nimic ales, pe un nivel de soluție. */
  despreNivel?: ReactNode;
  /** Spălătura fiecărui apartament, pe rangul lui — v. `Desen::culoriApartamente`.
   *  Numai pe `nivel`; se desenează cât e aprins comutatorul ei. */
  culoriApartamente?: readonly (string | undefined)[];
  /** Ce stă în colțul din dreapta-jos al pânzei — pe un nivel de soluție,
   *  comutatorul vederii vizualizatorului. */
  colt?: ReactNode;
  /** CÂND A FOST EDITAT ULTIMA OARĂ, gata scris. Stă în colțul din STÂNGA-jos,
   *  cerut acolo (21.10.2026): colțul drept e luat de comutatorul vederii.
   *  Gata scris, nu o dată brută — masa nu știe în ce limbă e ecranul, iar
   *  pagina o are deja. */
  editatLa?: ReactNode;
  /** Celelalte corpuri ale cotei, în jurul celui de pe masă — v.
   *  `Panza::vecini`, și cele trei de mai jos cu el. */
  vecini?: readonly Vecin[];
  laVecin?: (cheie: string, p: [number, number] | null) => void;
  cheieIncadrare?: string;
  cadruTot?: { x: number; y: number; w: number; h: number } | null;
}) {
  const t = useT();
  const {
    doc, docRef, plan, ales, alese, pereteleAlese, mobilaAlese, golurileAlese,
    aleseRef, mobilaAleseRef, alege, unealta, setUnealta,
    piesa, setPiesa, stransa, setStransa, panouStrans, setPanouStrans,
    dublateAcum,
    incadrari, vedere, setVedere, mod, setMod,
    aparat, setAparat, finisaj, setFinisaj, raportBagheta, setRaportBagheta,
    aratare, calc, schimbaCalcul, araAxe, araEtichete, layere, schimbaLayerul,
    layereDeschise,
    araCote, araApartamente, pornesteGest, schimba, aplica, terminaGest,
    curbPermis, setCurbPermis, bagheta, dubleazaAles, oglindeste, roteste,
  } = m;
  /** The layers panel stands beside the properties on a module's 2D canvas
   *  only: the level editor and the 3D view have no layers. */
  const cuLayere = layereDeschise && fel !== "nivel" && vedere !== "3d";
  /** Writes a squaring as one undo step, its rooms moved by the squaring. */
  const indrepteaza = (f: (d: TronsonDoc) => { doc: TronsonDoc; report: RepairReport }) =>
    aplica((d) => f(d).doc, { faraCamere: true });
  if (!doc || !plan) return null;
  return (
    <>
      {/* CE A PUS BAGHETA, o dată. Un rezultat care se vede e un rezultat care
          se poate verifica: «17 piese și 34 de aparate» îți spune imediat dacă
          a prins toate camerele sau doar jumătate. Se stinge la prima atingere
          a desenului — e o confirmare, nu o stare a modulului. */}
      {raportBagheta && (
        <div className="ob-ed-raport">
          <span className="ob-ed-bagheta-em"><Icoana nume="bagheta" marime={17} /></span>
          <span>{t("editor.bagheta_titlu", { raport: raportBagheta })}</span>
          <button onClick={() => setRaportBagheta(null)}
                  title={t("editor.inchide")}><Icoana nume="inchide" /></button>
        </div>
      )}

      {/* ---- corpul ---------------------------------------------------- */}
      {/* PE NIVELUL UNEI SOLUȚII NU EXISTĂ PALETĂ (Stefan, 23.09.2026: «când mă
          uit la soluțiile de arhitectură nu vreau deloc meniul de obiecte pe
          care le pot adăuga»). Ce e desenat se alege, se mută și se scrie în
          soluție; nimic nou nu se pune de aici. */}
      <div className={`ob-ed-corp${fel === "nivel" ? " fara-paleta"
                        : stransa ? " stransa" : ""}`
                      + (panouStrans ? " panou-strans" : "")
                      + (cuLayere ? " cu-layere" : "")}>
        {fel === "nivel" ? null : mod === "design" ? (
          <PaletaDesign
            unealta={unealta} laUnealta={(u) => {
              setUnealta(u);
              // Pensula NU golește selecția: ea vopsește ce e sub cursor, iar
              // camera aleasă rămâne cea al cărei panou se citește la dreapta.
              if (u !== "sageata" && u !== "pensula") alege(null);
            }}
            piesa={piesa} laPiesa={setPiesa}
            aparat={aparat} laAparat={setAparat}
            finisaj={finisaj} laFinisaj={setFinisaj}
            laMobilier={() => bagheta("mobilier")}
            laElectric={() => bagheta("electric")}
            stransa={stransa} laStransa={() => setStransa((v) => !v)}
            doarCitire={doarCitire} />
        ) : (
          <Paleta unealta={unealta} laUnealta={(u) => {
            setUnealta(u);
            // O selecție rămasă aprinsă ar ține apucătoarele peste desen, iar ele
            // prind clickurile de plasare — exact ce nu trebuie cât pui ceva.
            if (u !== "sageata") alege(null);
          }} piesa={piesa} laPiesa={setPiesa}
             aparat={aparat} laAparat={setAparat}
             stransa={stransa} laStransa={() => setStransa((v) => !v)} />
        )}
        <div className="ob-ed-mijloc">
          {vedere !== "3d" ? (
            <Panza doc={doc} ales={ales} alese={alese} pereteleAlese={pereteleAlese}
                   dublateAcum={dublateAcum}
                   alegeLista={m.alegeLista}
                   mobilaAlese={mobilaAlese} golurileAlese={golurileAlese}
                   laAles={alege}
                   aratare={aratare}
                   laSchimbare={schimba}
                   laGestPornit={pornesteGest} laGestTerminat={terminaGest}
                   unealta={unealta} laUnealta={setUnealta} piesa={piesa}
                   curbPermis={ales?.fel === "perete"
                     && !!curbPermis[plan.pereti[ales.i]?.id ?? ""]}
                   araAxe={araAxe} araEtichete={araEtichete}
                   layere={fel === "nivel" ? undefined : layere}
                   araCote={araCote}
                   culoriApartamente={fel === "nivel" && araApartamente
                     ? culoriApartamente ?? null : null}
                   calc={calc}
                   doarCitire={doarCitire}
                   mod={mod}
                   aparat={aparat} finisaj={finisaj}
                   laFinisaj={(cam) => aplica(
                     (d) => pictaFinisaj(d, cam, { [finisaj.unde]: finisaj.cheie }))}
                   vecini={vecini} laVecin={laVecin}
                   cheieIncadrare={`${cheieIncadrare ?? ""}#${incadrari}`}
                   cadruTot={cadruTot}
                   fondNucleu={fel !== "nivel"}
                   />
          ) : (
            <div className="ob-ed-panza">
              <Suspense fallback={<div className="ob-ed-astept">
                                    {t("editor.se_incarca_macheta")}
                                  </div>}>
                <Randare3D doc={doc} ales={ales} laAles={alege} mod={mod} />
              </Suspense>
            </div>
          )}
          {/* BARA CALCULUI, peste planșă, jos: cât se vede din foaie, dacă
              se vede, și cât e de lată în metri — singura cifră din care se
              prinde o calibrare ieșită prost. Apare numai când chiar e o
              foaie pusă. */}
          {calc && vedere !== "3d" && (
            <BaraCalcului calc={calc} laSchimbare={schimbaCalcul}
                          laScoatere={() => schimbaCalcul(null)} />
          )}
          {unealta !== "sageata" && vedere !== "3d" && (
            <div className="ob-ed-unealta-activa">
              <b>{unealta === "pensula"
                ? t(finisaj.unde === "floor"
                    ? "editor.unealta.pensula_pardoseala"
                    : "editor.unealta.pensula_pereti")
                : t(NUME_UNEALTA[unealta as keyof typeof NUME_UNEALTA])}</b>
              <span>{t(SFAT_UNEALTA[unealta as Exclude<Unealta, "sageata">])}</span>
              <button onClick={() => setUnealta("sageata")}>{t("editor.esc")}</button>
            </div>
          )}
          {colt && <div className="card colt-vedere ob-ed-colt">{colt}</div>}
          {editatLa && <div className="ob-ed-editat">{editatLa}</div>}
          <div className="ob-ed-vederi">
            <div className="ob-seg">
              {([["plan", "editor.vedere_plan"],
                 ["design", "editor.vedere_design"],
                 ["machete", "editor.vedere_macheta"]] as const).map(([k, n2]) => (
                <button key={k}
                        className={(k === "machete" ? vedere === "3d"
                          : vedere === "2d" && mod === k) ? "on" : ""}
                        onClick={() => {
                          if (k === "machete") { setVedere("3d"); return; }
                          setVedere("2d");
                          setMod(k);
                          // UNEALTA NU TRECE DINTR-UN MOD ÎN ALTUL. Nu
                          // fiindcă n-ar avea ce face — aparatele se pun și din
                          // paleta de plan, grupa «Instalații electrice» —, ci
                          // fiindcă paletele sunt altele: unealta aleasă
                          // rămânea aprinsă fără niciun buton apăsat pe ecran,
                          // iar prima apăsare pe desen punea ceva ce nimeni nu
                          // mai ceruse.
                          setUnealta("sageata");
                        }}>{t(n2)}</button>
              ))}
            </div>
          </div>
          {/* bara contextuală a selecției — «Duplicate / Flip / Delete / Lock» */}
          {ales && vedere !== "3d" && !doarCitire && (
            <div className="ob-ed-context">
              {/* AXA NU SE DUBLEAZĂ. Ea se deduce din ziduri, iar a doua pe
                  același loc ar fi aceeași axă — butonul n-ar fi făcut nimic.
                  Scos, nu stins: stinsul răspunde la o întrebare pe care
                  nimeni n-o pune despre o linie de trasare. */}
              {/* ACEEAȘI FAPTĂ CA TASTA `CO`, nu una scrisă a doua oară aici.
                  Scrisă separat, ea lucra pe `ales` de-a dreptul și sărea peste
                  `caIntreg`: apăsat cu o RAMPĂ aleasă, butonul chema `dubleaza`
                  pe un `scaraParte`, pe care el nu-l cunoaște, deci nu se
                  întâmpla nimic — «am încercat să selectez o parte a scării și
                  să fac dublează și nu merge» (21.10.2026). Tot de-aia nu intra
                  nici în istoric prin `aplica`. */}
              {/* NICI COTA NU SE DUBLEAZĂ, din același motiv ca axa: a doua
                  cotă peste prima ar măsura exact aceeași față, deci butonul
                  n-ar face nimic. Scos, nu stins. */}
              {ales.fel !== "ax" && ales.fel !== "cota"
                && <button onClick={dubleazaAles}>{t("editor.dubleaza")}</button>}
              {/* CELE PATRU ÎNTOARCERI — v. `model::intoarcerile`.
                  Un sfert de tur într-o parte, unul în cealaltă, oglindirea pe
                  orizontală, oglindirea pe verticală. ACELEAȘI PATRU, ÎN
                  ACEEAȘI ORDINE, PE ORICE E ALES: zid, ușă, cameră, piesă de
                  mobilier, aparat, nucleu, contur.

                  CE SE APĂRĂ AICI E REGULA, nu comenzile. Cine a învățat că
                  un obiect se întoarce din bara care-i apare peste el caută
                  același lucru pe următorul; o comandă găsită o dată în bară, o
                  dată în panoul din dreapta și o dată nicăieri nu-l învață unde
                  e, îl învață că «uneori se poate».

                  STINSE, NU LIPSĂ, unde întoarcerea n-are ce face: motivul stă
                  în tooltip («camera nu se rotește: se rotesc zidurile ei»). Un
                  buton stins care spune de ce răspunde la întrebare; unul lipsă
                  o lasă deschisă.

                  DOAR ICOANE. Bara poartă deja patru-cinci butoane cu text, iar
                  patru cuvinte în plus ar fi rupt-o pe două rânduri pe o pânză
                  îngustă. Cele patru sunt un GRUP de unelte, se citesc din
                  formă, iar numele întreg — și, pe ușă, numele comenzii pe care
                  o fac de fapt — e în tooltip.

                  OGLINDIREA ȚINE O STARE și pe piese: butonul rămâne aprins cât
                  ține steagul, fiindcă pe un obiect aproape simetric e singurul
                  loc din care se vede că e întors. */}
              {(() => {
                const int = intoarcerile(doc, ales);
                /* Cheile vin din model, care nu cunoaște catalogul de
                   traduceri — și nici n-are de ce: el spune DE CE nu se poate,
                   nu în ce limbă. Că fiecare cheie există chiar în catalog o
                   ține proba (`intoarceri.test`), nu tipul. */
                const titlu = (cheie: Cheie, i: Intoarcere) => {
                  const ce = (i.deCeNu ?? i.nume) as Cheie | undefined;
                  return ce ? `${t(cheie)} — ${t(ce)}` : t(cheie);
                };
                return (
                  <span className="ob-ed-grup">
                    <button disabled={!!int.rotire.deCeNu}
                            title={titlu("editor.roteste_stanga", int.rotire)}
                            aria-label={t("editor.roteste_stanga")}
                            onClick={() => roteste(-90)}>
                      <Icoana nume="rotireStanga" marime={15} />
                    </button>
                    <button disabled={!!int.rotire.deCeNu}
                            title={titlu("editor.roteste_dreapta", int.rotire)}
                            aria-label={t("editor.roteste_dreapta")}
                            onClick={() => roteste(90)}>
                      <Icoana nume="rotireDreapta" marime={15} />
                    </button>
                    <button disabled={!!int.oglH.deCeNu}
                            className={int.oglH.aprins ? "on" : ""}
                            aria-pressed={int.oglH.aprins}
                            title={titlu("editor.intoarceri.ogl_h", int.oglH)}
                            aria-label={t("editor.oglindeste_h")}
                            onClick={() => oglindeste("h")}>
                      <Icoana nume="oglindaX" marime={15} />
                    </button>
                    <button disabled={!!int.oglV.deCeNu}
                            className={int.oglV.aprins ? "on" : ""}
                            aria-pressed={int.oglV.aprins}
                            title={titlu("editor.intoarceri.ogl_v", int.oglV)}
                            aria-label={t("editor.oglindeste_v")}
                            onClick={() => oglindeste("v")}>
                      <Icoana nume="oglindaY" marime={15} />
                    </button>
                  </span>
                );
              })()}
              <button onClick={() => {
                        aplica((d) => stergeAlesele(d, m.aleseleToate()));
                        alege(null);
                      }}>
                {t("editor.sterge")}
              </button>
              {/* BLOCAREA UNEI AXE, și în bara neagră, nu doar în panou.
                  Cerut de arhitect (26.10.2026), după ce butonul a apărut în
                  panoul din dreapta: «pune butonul de blochează/deblochează și
                  pe panoul mic negru de sus». E chiar regula scrisă mai sus
                  despre întoarceri — cine a învățat că o faptă asupra
                  obiectului ales stă în bara care-i apare peste el o caută
                  acolo și pe următorul —, iar axa se blochează tocmai ca să nu
                  mai fie atinsă din greșeală: drumul cel mai scurt până la
                  comandă e locul unde îi stă mâna.

                  TEXT, NU ICOANĂ, și spune STAREA în care intră: un buton de
                  blocare care arată un lacăt nu lămurește dacă lacătul e ce
                  ești sau ce vei fi. */}
              {ales.fel === "ax" && (() => {
                const ax = axele(doc)[ales.i];
                if (!ax) return null;
                const blocat = axaBlocata(doc, ax.nume);
                return (
                  <button aria-pressed={blocat}
                          onClick={() => aplica(
                            (d) => blocheazaAxul(d, ax.nume, !blocat))}
                          title={t("prop.ax_blocat_nota")}>
                    {t(blocat ? "prop.ax_deblocheaza" : "prop.ax_blocheaza")}
                  </button>
                );
              })()}
              {/* UNEȘTE CAMERELE ALESE. Apare abia cu două adunate cu ⌘ —
                  singurul gest la care se pot alege mai multe —, fiindcă o
                  singură cameră n-are cu ce se uni.

                  NU LIPEȘTE DOUĂ POLIGOANE: desface ce le desparte și scoate
                  din listă camera absorbită, iar poligonul camerei rămase îl
                  face derivarea din pereți, ca pe oricare altul (v.
                  `model::uneste`). Camera care rămâne e cea aleasă ÎNTÂI, cu
                  felul și aria ei — iar unirea se cere de-aici, fiindcă
                  derivarea nu unește niciodată singură două camere. */}
              {alese.length > 1 && (
                <button onClick={() => {
                  aplica((d) => uneste(d, aleseRef.current));
                  alege(null);
                }}>{t("editor.uneste_camerele")}</button>
              )}
              {/* UNEȘTE PIESELE ALESE ÎNTR-UN ANSAMBLU — v. `model::uneMobila`.
                  Apare, ca unirea camerelor, abia cu două adunate cu Shift.

                  NU E ACELAȘI LUCRU CU UNIREA CAMERELOR, deși butoanele stau
                  alături: aia desface pereții dintre ele și face o cameră din
                  două, asta nu atinge geometria deloc — scrie doar că piesele
                  se mișcă de-acum împreună. De-aia și textul e «elemente», nu
                  «camere», și de-aia desfacerea e nedistructivă. */}
              {mobilaAlese.length > 1 && (
                <button onClick={() => {
                  aplica((d) => uneMobila(d, mobilaAleseRef.current));
                }}>{t("editor.uneste_elementele")}</button>
              )}
              {/* CE SE FACE CU O PIESĂ STĂ ÎN BARA DE DEASUPRA EI, lângă
                  «Șterge» — nu în panoul din dreapta.

                  Au fost întâi în panou, într-un rând numit «Teanc». Verdictul
                  arhitectului: «the teanc buttons are pathethic. It shouldn't
                  be in the right menu. When selecting a furniture piece we
                  should see in it's top menu like the bar» (19.10.2026). Are
                  dreptate și dincolo de gust: panoul din dreapta e pentru
                  CIFRELE piesei — mărime, unghi, înălțime —, iar astea sunt
                  COMENZI, adică exact ce stă deja în bara de sus pentru orice
                  alt obiect ales. Panoul nu le mai ține; un singur loc. */}
              {/* …ȘI PE ZIDURI. Zidurile stau deja peste mobilă (v. `Desen`),
                  dar între ele ordinea era cea din document: două suprapuse se
                  acopereau după cine fusese scris întâi, și nu se putea alege.
                  Cerut anume (26.10.2026): «nu văd un buton de adu în față pe
                  pereți, și ar trebui să existe». */}
              {ales.fel === "perete" && (
                <button onClick={() => aplica(
                  (d) => rangPeretiMulti(d, pereteleAlese.length
                    ? pereteleAlese : [ales.i], "fata"))}
                        title={t("prop.piesa.teanc_nota")}>
                  {t("prop.piesa.in_fata")}
                </button>
              )}
              {ales.fel === "perete" && (
                <button onClick={() => aplica(
                  (d) => rangPeretiMulti(d, pereteleAlese.length
                    ? pereteleAlese : [ales.i], "spate"))}
                        title={t("prop.piesa.teanc_nota")}>
                  {t("prop.piesa.in_spate")}
                </button>
              )}
              {ales.fel === "mobila" && (
                <button onClick={() => aplica((d) => rangMobilaMulte(d, mobilaAlese, "fata"))}
                        title={t("prop.piesa.teanc_nota")}>
                  {t("prop.piesa.in_fata")}
                </button>
              )}
              {ales.fel === "mobila" && (
                <button onClick={() => aplica((d) => rangMobilaMulte(d, mobilaAlese, "spate"))}
                        title={t("prop.piesa.teanc_nota")}>
                  {t("prop.piesa.in_spate")}
                </button>
              )}
              {/* SEPARĂ, numai pe o piesă care CHIAR e într-un ansamblu: pe una
                  singură butonul n-ar avea ce desface, iar un buton care nu
                  face nimic e mai rău decât niciun buton. */}
              {ales.fel === "mobila" && plan.piese[ales.i]?.grup && (
                <button onClick={() => aplica((d) => separaMobila(d, ales.i))}
                        title={t("prop.piesa.ansamblu_nota")}>
                  {t("prop.piesa.separa")}
                </button>
              )}
              {/* AMENAJAREA CAMEREI ALESE, ÎN DOUĂ: mobilierul (regulile din
                  `design/mobilare`) și instalația (rețetele din `design/auto`).
                  Fiecare înlocuiește ce e în cameră din partea LUI și o lasă pe
                  cealaltă neatinsă; fiecare e un pas de istoric, deci ⌘Z
                  întoarce exact apăsarea făcută.

                  DOUĂ BUTOANE, NU UNUL — cerut pe 19.10.2026, aceeași cerere ca
                  la bagheta modulului pe 04.10.2026 («amenajează camera și
                  amenajează electrica separat»). Au fost unul singur fiindcă
                  invers era mai rău: butonul punea DOAR mobila, iar camera
                  rămânea fără nicio priză («când dau pe furnish nu îmi pune
                  deloc partea electrică»). Răspunsul de atunci — să le lipim —
                  rezolva lipsa, dar lua alegerea: cine a mutat mobila de mână o
                  pierdea ca să-și tragă instalația. Acum sunt două comenzi care
                  se văd, ca în paleta Design.

                  ORDINEA CONTEAZĂ, și de-aia mobilierul e primul: rețeta de
                  instalație se citește CHIAR din mobila așezată (priza e la
                  capul patului fiindcă patul e acolo), deci pe o cameră goală
                  instalația singură dă doar lumina și întrerupătorul. */}
              {ales.fel === "camera" && (
                <button onClick={() => aplica((d) => mobileazaCamera(d, ales.i))}
                        title={t("editor.mobileaza_camera_titlu")}>
                  {t("editor.mobileaza_camera")}
                </button>
              )}
              {ales.fel === "camera" && (
                <button onClick={() => aplica((d) => puneInstalatiaCamerei(d, ales.i))}
                        title={t("prop.electrice.pune_titlu")}>
                  {t("editor.instalatia_camerei")}
                </button>
              )}
              {/* SENSUL PERETELUI, în bara de deasupra lui — nu doar în panou.
                  Un zid n-are, pe desen, niciun semn că ar avea un sens, iar
                  cine vede straturile ieșite pe dos caută întoarcerea CHIAR
                  ACOLO, peste zidul ales. Aceeași comandă, două locuri: panoul
                  o are sub listă, unde se vede ce face. */}
              {ales.fel === "perete" && (
                /* FĂRĂ ICOANĂ: cele două oglinzi ale grupului o poartă, iar
                   comanda asta e alta — întoarce ALCĂTUIREA pe tot runul, nu
                   oglindește bucata aleasă. Un semn în două înțelesuri, la doi
                   centimetri unul de altul, nu mai e un semn. */
                <button title={t("prop.straturi.intoarce_titlu")}
                        onClick={() => aplica((d) => intoarcePeretele(d, ales.i))}>
                  {t("editor.intoarce_peretele")}
                </button>
              )}
              {/* ADUCE O AXĂ PE ZIDUL ALES — v. `model::adaugaAxul`.
                  Aici, nu în paletă: o axă nu se desenează pe pânză, se pune
                  PE un perete, iar peretele e deja ales când întrebi.

                  STINS CÂND ARE DEJA UNA, cu motivul în tooltip, ca peste tot
                  în bara asta: un buton lipsă lasă întrebarea deschisă, unul
                  stins o închide. Scosul e la capătul axei, pe planșă (v.
                  `Panza`), fiindcă acolo se vede ce anume scoți. */}
              {ales.fel === "perete" && (
                <button disabled={areAxa(doc, ales.i)}
                        title={areAxa(doc, ales.i)
                          ? t("editor.ax_are_deja") : t("panza.ax_adauga")}
                        onClick={() => aplica((d) => adaugaAxul(d, ales.i))}>
                  {t("editor.adauga_ax")}
                </button>
              )}
              {/* SENSUL DE DESCHIDERE AL UȘII NU ARE BUTOANE ALE LUI: el E
                  oglindirea. «Întoarce sensul» mută foaia de pe o parte a
                  zidului pe alta, «oglindește balamaua» o mută dintr-un toc în
                  celălalt — adică oglinda de-a curmezișul zidului și cea de-a
                  lungul lui. Le fac deci H și V, care dintre ele o face pe care
                  citindu-se din direcția zidului (v. `model::oglindesteGolul`),
                  iar numele comenzii stă în tooltip. */}
              <button onClick={() => alege(null)}>{t("editor.deselecteaza")}</button>
            </div>
          )}
        </div>
        {cuLayere && <LayersPanel layere={layere} laSchimbare={schimbaLayerul} />}
        <Proprietati
          strans={panouStrans} laStrans={() => setPanouStrans((v) => !v)}
          laAxeToate={(blocat) => aplica((d) => {
            let r = d;
            for (const a of axele(d)) r = blocheazaAxul(r, a.nume, blocat);
            return r;
          })}
          capeteleLibere={ales?.fel === "perete" && capeteLibere(doc, ales.i)}
          laCapeteLibere={(liber) => {
            if (ales?.fel === "perete") {
              const i = ales.i;
              aplica((d) => lasaCapeteleLibere(d, i, liber));
            }
          }}
          doc={doc} plan={plan} ales={ales} doarCitire={doarCitire}
          fel={fel} despreNivel={despreNivel}
          etichete={etichete?.etichete} posibileEtichete={etichete?.posibile}
          scriuEtichete={etichete?.lucrez}
          laEtichete={doarCitire ? undefined : etichete?.laSchimbare}
          laPerete={(ce) => ales?.fel === "perete"
            && aplica((d) => schimbaPeretele(d, ales.i, ce))}
          laCurbura={(b) => ales?.fel === "perete"
            && aplica((d) => curbeazaPeretele(d, ales.i, b))}
          /* STRATURILE. Fiecare gest e un pas de istoric, ca oricare altul din
             panou: un strat pus din greșeală se scoate cu ⌘Z, nu ștergându-l cu
             mâna — și trebuie să fie așa, fiindcă un strat adăugat MUTĂ zidul
             (grosimea lui e suma straturilor), deci nu e o schimbare pe care
             ochiul o poate întoarce singur. */
          laStratNou={(kind, unde) => ales?.fel === "perete"
            && aplica((d) => adaugaStrat(d, ales.i, kind, unde))}
          laStrat={(k, ce) => ales?.fel === "perete"
            && aplica((d) => schimbaStratul(d, ales.i, k, ce))}
          laStergeStratul={(k) => ales?.fel === "perete"
            && aplica((d) => stergeStratul(d, ales.i, k))}
          laMutaStratul={(k, spre) => ales?.fel === "perete"
            && aplica((d) => mutaStratul(d, ales.i, k, spre))}
          laIntorsPeretele={() => ales?.fel === "perete"
            && aplica((d) => intoarcePeretele(d, ales.i))}
          curbat={ales?.fel === "perete"
            && (Math.abs(plan.pereti[ales.i]?.bulge ?? 0) > 1e-6
                || !!curbPermis[plan.pereti[ales.i]?.id ?? ""])}
          laCurbat={(on) => {
            if (ales?.fel !== "perete") return;
            const w = plan.pereti[ales.i];
            if (!w) return;
            setCurbPermis((c) => ({ ...c, [w.id]: on }));
            // Bifa scoasă îl face drept la loc: un zid curb fără voie să fie
            // curb n-are ce căuta în document.
            if (!on && Math.abs(w.bulge) > 1e-6) {
              aplica((d) => curbeazaPeretele(d, ales.i, 0));
            }
          }}
          laLungime={(l) => ales?.fel === "perete" && aplica((d) => {
            // Lungimea scrisă se face o mutare de capăt: al doilea capăt pleacă
            // pe lungul zidului cu diferența. Prin `mutaCapatulSingur` și nu
            // scriind direct poligonul, ca zidul să se poarte exact ca atunci
            // când e tras de mână — un editor cu două căi de a face același
            // lucru are, mai devreme sau mai târziu, două rezultate. Fără
            // lipire: cifra scrisă e cifra vrută.
            // CIFRA SCRISĂ E CIFRA CARE INTRĂ, ȘI SE VERIFICĂ.
            //
            // Lungimea arătată e a CONTURULUI zidului (`osiaPeretelui(inel)`),
            // iar mutarea se aplică AXULUI lui. Cele două nu se schimbă cu
            // același pas: unde capătul e retezat sau prelungit de un vecin,
            // axul dus cu `dl` nu lungește conturul cu `dl`, iar câmpul se
            // reciteşte cu altă cifră decât cea scrisă — «1.6 switches to
            // 1.55» (19.10.2026).
            //
            // Deci nu se presupune că o mutare dă lungimea cerută: se mută, se
            // măsoară ce-a ieșit, și se corectează. Două-trei treceri ajung,
            // fiindcă greșeala se împuținează de fiecare dată; se iese când
            // conturul e la sub un milimetru de cerut, sau când o trecere nu
            // mai îmbunătățește nimic (zidul s-a oprit în ceva și mai departe
            // n-are unde).
            let doc2 = d;
            let cel = Infinity;
            for (let k = 0; k < 4; k++) {
              const p2 = deriva(doc2).pereti[ales.i];
              if (!p2) break;
              const dl = l - lungimeaInelului(p2.inel);
              if (Math.abs(dl) < 1e-3) break;
              if (Math.abs(dl) >= cel - 1e-9) break;   // nu mai progresează
              cel = Math.abs(dl);
              // DE CARE PARTE CREȘTE. Până acum pleca MEREU capătul 1, orice
              // ar fi fost de partea aia — deci un zid prins acolo și liber
              // dincoace se lungea chiar în vecinul lui. Cerut de arhitect
              // (20.10.2026): «it should lengthen the wall or shorten it from
              // the side of the wall that is not connected to another wall. If
              // a wall is connected on both sides it should increase or
              // decrease equally from both sides».
              //
              // SENSUL VINE DIN AX, nu din `lungPeX`: capătul 1 nu e neapărat
              // cel „mare", iar presupus așa, scrisul unei lungimi mai mari
              // scurta zidul la fiecare a doua orientare.
              const [qa, qb] = axaPeretelui(doc2.plan!.walls![ales.i]);
              const L = Math.hypot(qb[0] - qa[0], qb[1] - qa[1]) || 1;
              const dir: [number, number] = [(qb[0] - qa[0]) / L,
                                             (qb[1] - qa[1]) / L];
              const prinse = capetePrinse(doc2, ales.i);
              // Capătul 1 crește pe direcția axului, capătul 0 împotriva ei.
              const pasul = (capat: 0 | 1, cat: number): [number, number] =>
                (capat === 1 ? [dir[0] * cat, dir[1] * cat]
                  : [-dir[0] * cat, -dir[1] * cat]);
              // AMÂNDOUĂ PRINSE: jumătate de fiecare parte, ca zidul să
              // rămână centrat pe ce era. AMÂNDOUĂ LIBERE: pleacă capătul 1,
              // ca până acum — pe un zid de sine stătător alegerea e o
              // convenție, iar asta e cea veche.
              const muta: [0 | 1, number][] =
                prinse[0] && prinse[1] ? [[0, dl / 2], [1, dl / 2]]
                  : prinse[1] ? [[0, dl]]
                    : [[1, dl]];
              let urm = doc2;
              for (const [capat, cat] of muta) {
                urm = mutaCapatulSingur(urm, ales.i, capat, pasul(capat, cat),
                                        false);
              }
              if (urm === doc2) break;
              doc2 = urm;
            }
            return doc2;
          })}
          /* UNGHIUL SCRIS și ÎNDREPTAREA. Amândouă rotesc zidul în jurul
             mijlocului lui — v. `model::roteastePeretele`. Cerute după un
             perete rămas la 3,8° pe M1B, pe care nimic nu-l mai aducea drept. */
          laUnghi={(g) => ales?.fel === "perete"
            && aplica((d) => {
              // The field shows the architect's reading; the wall turns to the
              // direction nearest its own AXIS, so its ends keep their order.
              const w = d.plan?.walls?.[ales.i];
              return w ? roteastePeretele(d, ales.i, unghiulDinCitit(g, unghiulPeretelui(w))) : d;
            })}
          laIndreptat={() => ales?.fel === "perete"
            && indrepteaza((d) => indreaptaPeretele45(d, ales.i))}
          laIndreptaTot={() => indrepteaza((d) => squareWalls(d))}
          /* Poziția scrisă mută BUCATA, nu linia: e chiar gestul de aliniere
             pentru care nu exista niciunul — v. `mutaPereteleSingur`. */
          laPozitie={(v) => ales?.fel === "perete" && aplica((d) => {
            const zid = plan.pereti[ales.i];
            if (!zid) return d;
            const o = osiaPeretelui(zid.inel);
            const dl = v - o.fete[0];
            if (Math.abs(dl) < 1e-6) return d;
            return mutaPereteleSingur(d, ales.i,
                                      o.axa === 0 ? [dl, 0] : [0, dl],
                                      { cuVecinii: true, dezlipeste: true });
          })}
          /* Frontul mută CAPĂTUL modulului, cu zid și camere, și abia apoi se
             scrie — ca regula cusăturii (front = ultima cameră + 0,30) să fie
             adevărată după gest, nu raportată la publicare. */
          laFront={(v) => aplica((d) => schimbaFrontul(d, v), { contur: true })}
          laBanda={(v) => aplica((d) => schimbaBanda(d, v), { contur: true })}
          laBrat={(ce) => aplica((d) => schimbaBratul(d, ce), { contur: true })}
          laParte={(ce) => ales?.fel === "nucleuParte"
            && aplica((d) => schimbaParteaNucleului(d, ales.i, ce))}
          laAx={(ce) => {
            if (ales?.fel !== "ax") return;
            const ax = axele(docRef.current!)[ales.i];
            if (!ax) return;
            if (ce.blocat != null) {
              aplica((d) => blocheazaAxul(d, ax.nume, ce.blocat!));
              return;
            }
            if (ce.libera) { aplica((d) => lungimeaAxeiLibera(d, ax.nume)); return; }
            if (ce.lungime == null) return;
            // CIFRA ȚINE MIJLOCUL PE LOC și crește în amândouă părțile: din
            // panou nu se spune DINSPRE CARE capăt se scurtează. Pe capete se
            // trage cu mâna, acolo partea se alege arătând-o.
            const [t0, t1] = intindereaAxei(ax, cutiaDesenului(plan));
            const m = (t0 + t1) / 2;
            aplica((d) => lungimeaAxei(d, ax.nume,
                                       m - ce.lungime! / 2, m + ce.lungime! / 2));
          }}
          laScaraParte={(ce) => ales?.fel === "scaraParte"
            && aplica((d) => schimbaScaraParte(d, ales.i, ales.parte, ales.k, ce,
                                               doc.storey_height_m ?? H_NIVEL))}
          /* ȘI CU O PARTE DE SCARĂ ALEASĂ — aceeași poartă ca `laMobila` de
             mai jos, și pentru acelaşi motiv. Pe desen, rampele și podestele
             stau PESTE dreptunghiul piesei (v. `Desen`), deci un clic pe o
             scară alege `scaraParte`, nu `mobila`: citind numai „mobila",
             poarta asta nu se deschidea NICIODATĂ de la un clic pe scară, iar
             contratreptele, pasul și adâncimea treptei arătau vii și nu făceau
             nimic — «scara custom nu funcționează, niciun câmp modificat nu
             pare să facă ceva; pare că nici celelalte scări nu funcționează»
             (26.10.2026). `scaraParte` poartă același `i`, al piesei. */
          laScara={(ce) => (ales?.fel === "mobila"
                            || ales?.fel === "scaraParte")
            // The flight's width holds the total width while the gap allows
            // (sheet row 20) — v. `model::schimbaLatimeaRampei`.
            && aplica((d) => (ce.podest
              ? adaugaPodest(d, ales.i, doc.storey_height_m ?? H_NIVEL)
              : ce.lat != null && ce.lat > 0
              ? schimbaLatimeaRampei(d, ales.i, ce.lat,
                                     doc.storey_height_m ?? H_NIVEL)
              : schimbaScara(d, ales.i, ce,
                             doc.storey_height_m ?? H_NIVEL)))}
          /* ȘI CU O PARTE DE SCARĂ ALEASĂ: panoul piesei se arată de-acum
             și atunci (v. `Proprietati`), iar poarta asta citea numai
             „mobila" — câmpurile s-ar fi văzut MOARTE, ceea ce e mai rău
             decât lipsa lor. `scaraParte` poartă același `i`, al piesei. */
          laMobila={(ce) => (ales?.fel === "mobila"
                             || ales?.fel === "scaraParte")
            && aplica((d) => schimbaMobila(d, ales.i, ce))}
          laLungimeaScarii={(v) => {
            if (ales?.fel !== "mobila" && ales?.fel !== "scaraParte") return;
            // A refused length changes nothing, so it records no undo step:
            // the lever returns the document it was given, and `aplica`
            // would have pushed a step before asking it.
            const d0 = m.docRef.current;
            if (!d0) return;
            const d1 = schimbaLungimeaTotala(d0, ales.i, v,
                                             doc.storey_height_m ?? H_NIVEL);
            if (d1 !== d0) aplica(() => d1);
          }}
          laLatimeaTotala={(v) => {
            if (ales?.fel !== "mobila" && ales?.fel !== "scaraParte") return;
            // as the length: a refusal records no undo step
            const d0 = m.docRef.current;
            if (!d0) return;
            const d1 = schimbaLatimeaTotala(d0, ales.i, v,
                                            doc.storey_height_m ?? H_NIVEL);
            if (d1 !== d0) aplica(() => d1);
          }}
          laCamera={(ce) => ales?.fel === "camera"
            && aplica((d) => (ce.afara
              ? schimbaExteriorul(d, ales.i, ce.afara)
              : schimbaCamera(d, ales.i, ce)))}
          laGol={(ce) => ales?.fel === "gol"
            && aplica((d) => schimbaGolul(d, ales.fel2, ales.i, ce))}
          laAparat={(ce) => ales?.fel === "aparat"
            && aplica((d) => schimbaAparat(d, ales.i, ce))}
          laDoza={(ce) => ales?.fel === "aparat"
            && aplica((d) => (ce.adauga != null
              ? adaugaInDoza(d, ales.i, ce.adauga)
              : ce.indice
                ? schimbaIndiceInDoza(d, ales.i, ce.indice.j, ce.indice.index)
                : scoateDinDoza(d, ales.i, ce.scoate!)))}
          /* ELECTRICELE DIN PANOU — v. nota de la `Electrice`. Toate trec prin
             `aplica`, deci fiecare e un pas de istoric și ⌘Z le întoarce. */
          laAles={alege}
          /* CAMERA RĂMÂNE ALEASĂ după fiecare din gesturile astea, și
             dinadins: lista din care se apasă e a ei, iar trecând selecția pe
             aparatul nou panoul s-ar schimba sub deget și al doilea aparat ar
             cere întâi re-alegerea camerei. Ce s-a pus se vede oricum — în
             listă și pe desen. */
          laAparatNou={(camera, kind) =>
            aplica((d) => adaugaAparatInCamera(d, camera, kind).doc)}
          laStergeAparatul={(i) => aplica((d) => sterge(d, { fel: "aparat", i }))}
          laInstalatiaCamerei={(camera) =>
            aplica((d) => puneInstalatiaCamerei(d, camera))}
          laDezlegat={(i) => aplica((d) => dezleagaAparatul(d, i))}
          laFinisaj={(cam, ce) => aplica((d) => pictaFinisaj(d, cam, ce))}
          mod={mod}
          laModul={(ce) => ce.nivel != null
            && aplica((d) => ({ ...d, storey_height_m: ce.nivel }))}
          /* CATEGORIA trece prin `aplica`, ca orice schimbare a documentului:
             e un pas de istoric (⌘Z o întoarce) și lasă modulul nesalvat, deci
             se scrie la «Salvează ciorna» ca restul. Serverul își ia `role` din
             documentul recalculat, iar cartonașul din colecție își derivă
             categoria din el — nu e nimic de scris pe lângă. */
          laCategorie={(c) => aplica((d) => schimbaCategoria(d, c))}
          /* NUCLEUL NOU trece prin `aplica`, ca orice schimbare: un pas de
             istoric, documentul rămâne nesalvat. Bucățile lui nu le face nimeni
             la salvare: se pun din paletă sau cu «Parte a nucleului» (v.
             `model::faNucleu`). */
          laNucleuNou={() => aplica((d) => faNucleu(d))}
          laParteNucleu={(on) => {
            const cine = ales;
            let dupa: Ales = cine;
            aplica((d) => {
              const r = on && (cine?.fel === "mobila" || cine?.fel === "scaraParte")
                ? parteDinPiesa(d, cine.i)
                : !on && cine?.fel === "nucleuParte" ? piesaDinParte(d, cine.i)
                  : { doc: d, ales: cine };
              dupa = r.ales;
              return r.doc;
            });
            alege(dupa);
          }}
          /* NUCLEUL are două feluri de mărimi, și se scriu în două locuri.
             CUTIA (lățime, adâncime) e GEOMETRIE: mută muchii, deci trece prin
             `schimbaNucleul`, care duce și camerele din jur — altfel ar rămâne
             între ele o fâșie a nimănui.
             MĂSURILE (adâncimea scării, câte lifturi, grosimea pereților lui)
             sunt DECIZIA și stau în `doc.core`; desenul lor îl reface serverul
             la salvare, ca la arii. */
          laNucleu={(ce) => aplica((d) => {
            if (ce.latime != null || ce.adancime != null) {
              return schimbaNucleul(d, { w: ce.latime, h: ce.adancime });
            }
            if (!d.core) return d;
            return {
              ...d,
              core: {
                ...d.core,
                ...(ce.scara != null ? { stair_depth_m: ce.scara } : {}),
                ...(ce.put != null ? { lift_depth_m: ce.put } : {}),
                ...(ce.lifturi != null ? { lifts: ce.lifturi } : {}),
                ...(ce.zid != null ? { wall_t_m: ce.zid } : {}),
              },
            };
          })} />
      </div>
    </>
  );
}
